'use client';

import React, { useState, useEffect, useRef, useCallback } from 'react';
import Link from 'next/link';
import {
  ArrowRight, TrendingUp, Target, PiggyBank, Shield, BarChart3, FileText,
  ChevronDown, Star, Lock, Globe, Activity, Sun, Moon, Check, X, Sparkles,
  Gift, ChevronRight, Bell, ChevronLeft, Menu } from 'lucide-react';
import AppLogo from '@/components/ui/AppLogo';
import PricingSection from './components/PricingSection';
import UrgencyBanner from './components/UrgencyBanner';
import ActivityNotification from './components/ActivityNotification';
import { useCmsContent } from '@/hooks/useCmsContent';
import { getTestimonialText, getFaqText, type CmsLang } from '@/lib/cmsContent';

type Lang = 'en' | 'ar' | 'fr';

/* ─── Translations ─────────────────────────────────────────── */
const t_en = {
  dir: 'ltr' as const,
  fontClass: '',
  nav: {
    features: 'Features', pricing: 'Pricing', reviews: 'Reviews', faq: 'FAQ',
    howItWorks: 'How It Works', security: 'Security',
    signIn: 'Sign In', getStarted: 'Get Started'
  },
  hero: {
    badge: 'Your Personal Financial Operating System',
    headline1: 'Your Money.',
    headline2: 'Your Plan.',
    headline3: 'Your Future.',
    sub: 'One simple platform to understand your money, build better financial habits, and move closer to the life you want.',
    cta1: 'Start Free — No Card Required',
    cta2: 'See How WealthOS Works',
    trust: ['7-day free trial', 'No credit card', 'Cancel anytime'],
    notif: "Great job! 🎉 You saved $240 this month."
  },
  dashboard: {
    greeting: 'Good morning, Sarah 👋',
    netWorth: 'Total Net Worth',
    growth: '↑ +6.42% this year',
    income: 'Income',
    expenses: 'Expenses',
    savings: 'Savings',
    savingsRate: 'Savings Rate',
    savedThisMonth: '$1,797 saved this month',
    recentTx: 'Recent Transactions',
    healthScore: 'Financial Health Score',
    good: '↑ Good',
    growthChart: 'Net Worth Growth',
    dreamHome: 'Dream Home',
    emergencyFund: 'Emergency Fund',
    travelFund: 'Travel Fund',
    months: ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'],
    transactions: [
    { name: 'Salary Deposit', amount: '+$6,420', color: '#22c55e', icon: '💼' },
    { name: 'Grocery Store', amount: '-$124', color: '#f59e0b', icon: '🛒' },
    { name: 'Netflix', amount: '-$15', color: '#ef4444', icon: '📺' },
    { name: 'Auto-Savings', amount: '+$640', color: '#22c55e', icon: '🐷' }],

    goals: [
    { label: 'Dream Home', pct: 73, color: '#C9A227', amount: '$18,400 / $25,000' },
    { label: 'Travel Fund', pct: 48, color: '#0ea5e9', amount: '$4,800 / $10,000' },
    { label: 'Emergency Fund', pct: 82, color: '#22c55e', amount: '$8,200 / $10,000' }],

    heroGoals: [
    { label: 'Dream Home', pct: 73, color: '#C9A227' },
    { label: 'Emergency Fund', pct: 82, color: '#22c55e' }],

    netWorthLabel: 'Net Worth',
    savingsRateLabel: '28% / month',
    cashFlow: 'Cash Flow',
    cashFlowIncome: 'Income: $6,420',
    cashFlowExpenses: 'Expenses: $3,180',
    wealthosInsight: 'WealthOS Insight',
    live: 'Live',
    wealthosDashboard: 'WealthOS Dashboard'
  },
  problem: {
    headline: "Money shouldn\'t feel this complicated.",
    sub: "Sound familiar? You\'re not alone.",
    items: [
    { icon: '🏦', title: 'Too many accounts', desc: 'Your money is scattered across different banks and apps.', color: '#ef4444' },
    { icon: '🔍', title: 'No clear picture', desc: "You know what you earn, but not where your money really goes.", color: '#f97316' },
    { icon: '🎯', title: 'Goals without a plan', desc: "Saving for your future shouldn\'t feel like guesswork.", color: '#eab308' }]

  },
  solution: {
    badge: 'The Solution',
    headline: 'Meet WealthOS.',
    sub: 'Everything you need to understand, organize and grow your financial life — in one place.',
    insight: "You\'re on track to reach your emergency fund goal 2 months early. 🎉"
  },
  benefits: {
    badge: 'Why WealthOS',
    headline: 'Built around your life, not just your numbers.',
    items: [
    { title: 'Know where you stand.', desc: 'See your complete financial picture in seconds.', label: 'Net Worth Dashboard', color: '#C9A227' },
    { title: 'Save without thinking about it.', desc: 'Set your goal once and let WealthOS help you stay on track.', label: 'Automatic Savings Engine', color: '#22c55e' },
    { title: 'Turn numbers into decisions.', desc: 'Get clear reports and insights that help you make smarter financial choices.', label: 'Financial Reports', color: '#7c3aed' },
    { title: 'See your progress.', desc: 'Turn your financial goals into visible milestones.', label: 'Goal Progress', color: '#0ea5e9' }],

    miniLabels: {
      netWorth: 'Net Worth',
      autoSavingsGoal: 'Auto-Savings Goal',
      housing: 'Housing 45%',
      food: 'Food 20%',
      transport: 'Transport 15%',
      dreamHome: 'Dream Home',
      travel: 'Travel'
    }
  },
  lifestyle: {
    badge: 'Life Goals',
    headline: "Build the life you're saving for.",
    sub: "WealthOS isn\'t about numbers. It\'s about what those numbers allow you to achieve.",
    complete: 'complete',
    cards: [
    { emoji: '🏡', label: 'Dream Home', goal: '$18,400 / $25,000', pct: 73, img: "https://images.unsplash.com/photo-1684785619392-0bfa21177363", alt: 'Beautiful modern family home with manicured lawn and blue sky' },
    { emoji: '✈️', label: 'Travel the World', goal: '$6,200 / $10,000', pct: 62, img: "https://images.unsplash.com/photo-1634188323561-6c001706e48f", alt: 'Couple exploring a beautiful European city street at golden hour' },
    { emoji: '👨‍👩‍👧', label: 'Family Future', goal: '$26,450 / $40,000', pct: 71, img: "https://img.rocket.new/generatedImages/rocket_gen_img_1da7b19fe-1772690671456.png", alt: 'Happy family of four laughing together in a sunny park' },
    { emoji: '🌅', label: 'Financial Freedom', goal: '$62,000 / $100,000', pct: 62, img: "https://images.unsplash.com/photo-1689164817640-44afb91c74c1", alt: 'Person standing triumphantly on a mountain peak at sunrise' }]

  },
  beforeAfter: {
    headline: 'Stop managing your money everywhere.',
    sub: 'One platform. Total clarity.',
    before: { title: 'Before WealthOS', items: ['Multiple bank apps', 'Spreadsheets & notes', 'Manual calculations', 'No clear overview', 'Constant guessing'] },
    after: { title: 'With WealthOS', items: ['Everything in one place', 'Clear financial picture', 'Automatic savings', 'Smart insights', 'Financial goals'] }
  },
  howItWorks: {
    badge: 'How It Works',
    headline: 'Up and running in minutes.',
    steps: [
    { num: '01', title: 'Connect', desc: 'Connect your financial accounts and assets securely in seconds.' },
    { num: '02', title: 'Understand', desc: 'See your complete financial picture — net worth, income, goals.' },
    { num: '03', title: 'Grow', desc: 'Build goals, track progress, and build better financial habits.' }]

  },
  calculator: {
    badge: 'Savings Calculator',
    headline: 'What could your savings become?',
    sub: 'Small decisions today can create meaningful progress tomorrow.',
    labelMonthly: 'Monthly savings ($)',
    labelYears: 'Years',
    labelRate: 'Expected annual growth (%)',
    result: 'You could have',
    yearsLabel: (y: number) => `in ${y} year${y !== 1 ? 's' : ''}`,
    growthLabel: (r: number) => `at ${r}% annual growth`,
    cta: 'Build My Financial Plan →'
  },
  testimonials: {
    badge: 'Social Proof',
    headline: 'Trusted by people building their financial future.',
    items: [
    { name: 'Sarah M.', role: 'Marketing Manager', stars: 5, quote: 'For the first time, I actually understand where my money is going. WealthOS made it so clear.', img: "https://img.rocket.new/generatedImages/rocket_gen_img_18dcf7862-1772562312913.png", imgAlt: 'Young professional woman smiling' },
    { name: 'James T.', role: 'Entrepreneur', stars: 5, quote: 'I finally have one place to see my entire financial picture. It changed how I think about money.', img: "https://images.unsplash.com/photo-1653855394894-4c941ebd3748", imgAlt: 'Young man in casual attire smiling' },
    { name: 'Emily R.', role: 'Teacher', stars: 5, quote: 'Seeing my progress made saving feel achievable. I hit my emergency fund goal in 8 months.', img: "https://img.rocket.new/generatedImages/rocket_gen_img_1e3dcc45f-1772203448327.png", imgAlt: 'Professional woman with confident smile' },
    { name: 'Ahmed R.', role: 'Financial Analyst', stars: 5, quote: "The net worth dashboard gives a clarity that spreadsheets never could. I recommend it to everyone.", img: "https://img.rocket.new/generatedImages/rocket_gen_img_15d75124c-1772213059804.png", imgAlt: 'Professional man in business casual' }]

  },
  security: {
    badge: 'Security',
    headline: 'Your financial data deserves serious protection.',
    items: [
    { icon: Lock, title: 'AES-256 Encryption', desc: 'All data encrypted at rest and in transit', color: '#22c55e' },
    { icon: Globe, title: 'Multi-Currency', desc: 'USD, EUR, and DZD with live conversion', color: '#C9A227' },
    { icon: Activity, title: '99.9% Uptime', desc: 'Reliable infrastructure you can count on', color: '#0ea5e9' },
    { icon: Shield, title: 'Secure Infrastructure', desc: 'Zero data selling, ever', color: '#22c55e' }]

  },
  faq: {
    badge: 'FAQ',
    headline: 'Questions answered.',
    items: [
    { q: 'Is my financial data secure?', a: 'Yes. All data is encrypted at rest and in transit using AES-256 and TLS 1.3. We never sell your data to third parties.' },
    { q: 'Can I cancel my subscription anytime?', a: 'Absolutely. Cancel anytime from your settings — no questions asked. Your data remains accessible on the Free plan.' },
    { q: 'What does the 7-day free trial include?', a: 'Full access to either Pro or Pro Max — all features unlocked, no credit card required. After 7 days, choose to subscribe or drop to Free.' },
    { q: 'Which currencies are supported?', a: 'WealthOS supports USD, EUR, and DZD (Algerian Dinar) with live exchange rate conversion across all assets and reports.' },
    { q: 'Can I export my data?', a: 'Pro users can export PDF reports. Pro Max users get PDF, Excel, and JSON exports — perfect for accountants or tax filing.' },
    { q: 'Is there a mobile app?', a: 'WealthOS is a fully responsive web app that works beautifully on any device. A dedicated mobile app is on our roadmap.' }]

  },
  finalCta: {
    headline: "Your future won\'t build itself.",
    sub: 'Start making smarter financial decisions today.',
    cta1: 'Create My Free Account →',
    cta2: 'Sign In',
    trust: ['No credit card', '7-day free trial', 'Cancel anytime']
  },
  footer: { rights: 'All rights reserved.', privacy: 'Privacy', terms: 'Terms', support: 'Support', cookies: 'Cookie Policy', refund: 'Refund Policy' },
  stickyCta: 'Start Free — No Card →',
  stats: [
  { value: 10, prefix: '', suffix: 'K+', label: 'Active Users' },
  { value: 2.4, prefix: '$', suffix: 'B', label: 'Assets Tracked' },
  { value: 98, prefix: '', suffix: '%', label: 'Satisfaction Rate' },
  { value: 4.9, prefix: '', suffix: '★', label: 'App Rating' }]

};

const t_ar = {
  dir: 'rtl' as const,
  fontClass: 'font-arabic',
  nav: {
    features: 'المميزات', pricing: 'الأسعار', reviews: 'التقييمات', faq: 'الأسئلة',
    howItWorks: 'كيف يعمل', security: 'الأمان',
    signIn: 'تسجيل الدخول', getStarted: 'ابدأ الآن'
  },
  hero: {
    badge: 'نظام التشغيل المالي الشخصي',
    headline1: 'أموالك.',
    headline2: 'خطتك.',
    headline3: 'مستقبلك.',
    sub: 'منصة واحدة تساعدك على فهم أموالك، بناء عادات مالية أفضل، والاقتراب من الحياة التي تريدها.',
    cta1: 'ابدأ مجاناً — لا تحتاج إلى بطاقة',
    cta2: 'اكتشف كيف يعمل WealthOS',
    trust: ['تجربة مجانية لمدة 7 أيام', 'لا تحتاج إلى بطاقة ائتمانية', 'يمكنك الإلغاء في أي وقت'],
    notif: "أحسنت! 🎉 لقد ادخرت 240$ هذا الشهر."
  },
  dashboard: {
    greeting: 'صباح الخير، سارة 👋',
    netWorth: 'صافي الثروة',
    growth: '↑ +6.42% هذا العام',
    income: 'الدخل',
    expenses: 'المصروفات',
    savings: 'الادخار',
    savingsRate: 'نسبة الادخار',
    savedThisMonth: 'ادخرت 1,797$ هذا الشهر',
    recentTx: 'المعاملات الأخيرة',
    healthScore: 'نقاط الصحة المالية',
    good: '↑ جيد',
    growthChart: 'نمو صافي الثروة',
    dreamHome: 'منزل الأحلام',
    emergencyFund: 'صندوق الطوارئ',
    travelFund: 'صندوق السفر',
    months: ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'],
    transactions: [
    { name: 'إيداع الراتب', amount: '+$6,420', color: '#22c55e', icon: '💼' },
    { name: 'متجر البقالة', amount: '-$124', color: '#f59e0b', icon: '🛒' },
    { name: 'Netflix', amount: '-$15', color: '#ef4444', icon: '📺' },
    { name: 'ادخار تلقائي', amount: '+$640', color: '#22c55e', icon: '🐷' }],

    goals: [
    { label: 'منزل الأحلام', pct: 73, color: '#C9A227', amount: '18,400$ / 25,000$' },
    { label: 'صندوق السفر', pct: 48, color: '#0ea5e9', amount: '4,800$ / 10,000$' },
    { label: 'صندوق الطوارئ', pct: 82, color: '#22c55e', amount: '8,200$ / 10,000$' }],

    heroGoals: [
    { label: 'منزل الأحلام', pct: 73, color: '#C9A227' },
    { label: 'صندوق الطوارئ', pct: 82, color: '#22c55e' }],

    netWorthLabel: 'صافي الثروة',
    savingsRateLabel: '28% / شهر',
    cashFlow: 'التدفق النقدي',
    cashFlowIncome: 'الدخل: $6,420',
    cashFlowExpenses: 'المصروفات: $3,180',
    wealthosInsight: 'رؤية WealthOS',
    live: 'مباشر',
    wealthosDashboard: 'لوحة تحكم WealthOS'
  },
  problem: {
    headline: "إدارة المال لا يجب أن تكون معقدة.",
    sub: "هل هذا مألوف لك؟ أنت لست وحدك.",
    items: [
    { icon: '🏦', title: 'حسابات كثيرة', desc: 'أموالك موزعة بين عدة بنوك وتطبيقات.', color: '#ef4444' },
    { icon: '🔍', title: 'لا توجد رؤية واضحة', desc: 'تعرف كم تكسب، لكن هل تعرف فعلاً أين تذهب أموالك؟', color: '#f97316' },
    { icon: '🎯', title: 'أهداف بدون خطة', desc: 'الادخار لمستقبلك لا يجب أن يكون مجرد تخمين.', color: '#eab308' }]

  },
  solution: {
    badge: 'الحل',
    headline: 'تعرّف على WealthOS.',
    sub: 'كل ما تحتاج إليه لفهم أموالك وتنظيمها وبناء مستقبل مالي أفضل — في مكان واحد.',
    insight: 'أنت في الطريق الصحيح للوصول إلى هدف صندوق الطوارئ قبل الموعد بشهرين. 🎉'
  },
  benefits: {
    badge: 'لماذا WealthOS',
    headline: 'مبني حول حياتك، وليس فقط أرقامك.',
    items: [
    { title: 'اعرف أين تقف.', desc: 'شاهد صورتك المالية الكاملة خلال ثوانٍ.', label: 'لوحة صافي الثروة', color: '#C9A227' },
    { title: 'ادخر دون الحاجة إلى التفكير في الأمر.', desc: 'حدد هدفك مرة واحدة ودع WealthOS يساعدك على البقاء في المسار الصحيح.', label: 'محرك الادخار التلقائي', color: '#22c55e' },
    { title: 'حوّل الأرقام إلى قرارات.', desc: 'احصل على تقارير ورؤى واضحة تساعدك على اتخاذ قرارات مالية أفضل.', label: 'التقارير المالية', color: '#7c3aed' },
    { title: 'شاهد تقدمك.', desc: 'حوّل أهدافك المالية إلى خطوات واضحة يمكنك رؤيتها.', label: 'تتبع الأهداف', color: '#0ea5e9' }],

    miniLabels: {
      netWorth: 'صافي الثروة',
      autoSavingsGoal: 'هدف الادخار التلقائي',
      housing: 'السكن 45%',
      food: 'الطعام 20%',
      transport: 'المواصلات 15%',
      dreamHome: 'منزل الأحلام',
      travel: 'السفر'
    }
  },
  lifestyle: {
    badge: 'أهداف الحياة',
    headline: 'ابنِ الحياة التي تدخر من أجلها.',
    sub: 'WealthOS ليس عن الأرقام. إنه عن ما تتيح لك تلك الأرقام تحقيقه.',
    complete: 'مكتمل',
    cards: [
    { emoji: '🏡', label: 'منزل الأحلام', goal: '18,400$ / 25,000$', pct: 73, img: "https://img.rocket.new/generatedImages/rocket_gen_img_153b8e8d2-1776064313881.png", alt: 'منزل عائلي حديث بحديقة خضراء وسماء زرقاء' },
    { emoji: '✈️', label: 'السفر حول العالم', goal: '6,200$ / 10,000$', pct: 62, img: "https://images.unsplash.com/photo-1600506287840-8a587340ca37", alt: 'زوجان يستكشفان شارعاً أوروبياً جميلاً عند الغروب' },
    { emoji: '👨‍👩‍👧', label: 'مستقبل العائلة', goal: '26,450$ / 40,000$', pct: 71, img: "https://images.unsplash.com/photo-1717596738517-69780c3a0024", alt: 'عائلة سعيدة من أربعة أفراد تضحك في الهواء الطلق' },
    { emoji: '🌅', label: 'الحرية المالية', goal: '62,000$ / 100,000$', pct: 62, img: "https://img.rocket.new/generatedImages/rocket_gen_img_17552d1f5-1767905632857.png", alt: 'شخص يقف على قمة جبل عند شروق الشمس' }]

  },
  beforeAfter: {
    headline: 'توقف عن إدارة أموالك في كل مكان.',
    sub: 'منصة واحدة. وضوح تام.',
    before: { title: 'قبل WealthOS', items: ['تطبيقات بنكية متعددة', 'جداول Excel والملاحظات', 'حسابات يدوية', 'لا توجد رؤية واضحة', 'تخمين مستمر'] },
    after: { title: 'مع WealthOS', items: ['كل شيء في مكان واحد', 'صورة مالية واضحة', 'ادخار تلقائي', 'رؤى ذكية', 'أهداف مالية'] }
  },
  howItWorks: {
    badge: 'كيف يعمل',
    headline: 'جاهز للعمل في دقائق.',
    steps: [
    { num: '01', title: 'اربط', desc: 'اربط حساباتك المالية وأصولك بأمان في ثوانٍ.' },
    { num: '02', title: 'افهم', desc: 'شاهد صورتك المالية الكاملة — صافي الثروة، الدخل، الأهداف.' },
    { num: '03', title: 'نمِّ', desc: 'أنشئ أهدافاً مالية وتتبع تقدمك وابنِ عادات مالية أفضل.' }]

  },
  calculator: {
    badge: 'حاسبة الادخار',
    headline: 'كم يمكن أن تصبح مدخراتك؟',
    sub: 'قرارات صغيرة اليوم يمكن أن تصنع تقدماً كبيراً غداً.',
    labelMonthly: 'المدخر الشهري ($)',
    labelYears: 'عدد السنوات',
    labelRate: 'النمو السنوي المتوقع (%)',
    result: 'يمكن أن تصل مدخراتك إلى',
    yearsLabel: (y: number) => `في ${y} ${y === 1 ? 'سنة' : 'سنوات'}`,
    growthLabel: (r: number) => `بنمو سنوي ${r}%`,
    cta: 'أنشئ خطتي المالية →'
  },
  testimonials: {
    badge: 'آراء المستخدمين',
    headline: 'يثق بنا أشخاص يبنون مستقبلهم المالي.',
    items: [
    { name: 'سارة م.', role: 'مديرة تسويق', stars: 5, quote: 'لأول مرة أصبحت أفهم فعلاً أين تذهب أموالي. WealthOS جعل الأمر واضحاً جداً.', img: "https://img.rocket.new/generatedImages/rocket_gen_img_1f290582d-1772705272953.png", imgAlt: 'امرأة مهنية شابة تبتسم' },
    { name: 'جيمس ت.', role: 'رائد أعمال', stars: 5, quote: 'أصبح لدي أخيراً مكان واحد أرى فيه صورتي المالية بالكامل. غيّر طريقة تفكيري في المال.', img: "https://images.unsplash.com/photo-1702316004281-9dc21e40e304", imgAlt: 'شاب بملابس غير رسمية يبتسم' },
    { name: 'إيميلي ر.', role: 'معلمة', stars: 5, quote: 'رؤية تقدمي جعلت الادخار يبدو ممكناً. حققت هدف صندوق الطوارئ في 8 أشهر.', img: "https://img.rocket.new/generatedImages/rocket_gen_img_1fd8f9058-1780968659007.png", imgAlt: 'امرأة مهنية بابتسامة واثقة' },
    { name: 'أحمد ر.', role: 'محلل مالي', stars: 5, quote: 'لوحة صافي الثروة تعطي وضوحاً لم تستطع جداول البيانات تقديمه أبداً. أنصح به الجميع.', img: "https://img.rocket.new/generatedImages/rocket_gen_img_117063bc9-1772200771519.png", imgAlt: 'رجل مهني بملابس عمل' }]

  },
  security: {
    badge: 'الأمان',
    headline: 'بياناتك المالية تستحق حماية حقيقية.',
    items: [
    { icon: Lock, title: 'تشفير AES-256', desc: 'جميع البيانات مشفرة أثناء التخزين والنقل', color: '#22c55e' },
    { icon: Globe, title: 'دعم العملات المتعددة', desc: 'USD وEUR وDZD مع تحويل فوري', color: '#C9A227' },
    { icon: Activity, title: 'موثوقية 99.9%', desc: 'بنية تحتية موثوقة يمكنك الاعتماد عليها', color: '#0ea5e9' },
    { icon: Shield, title: 'بنية تحتية آمنة', desc: 'لا نبيع بياناتك أبداً', color: '#22c55e' }]

  },
  faq: {
    badge: 'الأسئلة الشائعة',
    headline: 'إجابات على أسئلتك.',
    items: [
    { q: 'هل بياناتي المالية آمنة؟', a: 'نعم. جميع البيانات مشفرة أثناء التخزين والنقل باستخدام AES-256 وTLS 1.3. لا نبيع بياناتك لأطراف ثالثة أبداً.' },
    { q: 'هل يمكنني إلغاء الاشتراك في أي وقت؟', a: 'بالتأكيد. يمكنك الإلغاء في أي وقت من الإعدادات — دون أي أسئلة. تبقى بياناتك متاحة في الخطة المجانية.' },
    { q: 'ماذا تتضمن التجربة المجانية لمدة 7 أيام؟', a: 'وصول كامل إلى Pro أو Pro Max — جميع المميزات مفتوحة، لا تحتاج إلى بطاقة ائتمانية. بعد 7 أيام، اختر الاشتراك أو العودة للخطة المجانية.' },
    { q: 'ما العملات المدعومة؟', a: 'يدعم WealthOS USD وEUR وDZD (الدينار الجزائري) مع تحويل أسعار الصرف الفوري عبر جميع الأصول والتقارير.' },
    { q: 'هل يمكنني تصدير بياناتي؟', a: 'يمكن لمستخدمي Pro تصدير تقارير PDF. يحصل مستخدمو Pro Max على تصدير PDF وExcel وJSON — مثالي للمحاسبين أو تقديم الضرائب.' },
    { q: 'هل يوجد تطبيق للهاتف؟', a: 'WealthOS هو تطبيق ويب متجاوب بالكامل يعمل بشكل رائع على أي جهاز. تطبيق الهاتف المخصص موجود في خارطة طريقنا.' }]

  },
  finalCta: {
    headline: 'مستقبلك لن يبني نفسه.',
    sub: 'ابدأ اليوم باتخاذ قرارات مالية أكثر ذكاءً.',
    cta1: 'أنشئ حسابي المجاني →',
    cta2: 'تسجيل الدخول',
    trust: ['لا تحتاج إلى بطاقة', 'تجربة 7 أيام', 'يمكنك الإلغاء في أي وقت']
  },
  footer: { rights: 'جميع الحقوق محفوظة.', privacy: 'الخصوصية', terms: 'الشروط', support: 'الدعم', cookies: 'سياسة الكوكيز', refund: 'سياسة الاسترداد' },
  stickyCta: 'ابدأ مجاناً — لا بطاقة →',
  stats: [
  { value: 10, prefix: '', suffix: 'K+', label: 'مستخدم نشط' },
  { value: 2.4, prefix: '$', suffix: 'B', label: 'أصول متابَعة' },
  { value: 98, prefix: '', suffix: '%', label: 'نسبة الرضا' },
  { value: 4.9, prefix: '', suffix: '★', label: 'متوسط التقييم' }]

};

/* ─── French Translations ───────────────────────────────────── */
const t_fr = {
  dir: 'ltr' as const,
  fontClass: '',
  nav: {
    features: 'Fonctionnalités', pricing: 'Tarifs', reviews: 'Avis', faq: 'FAQ',
    howItWorks: 'Comment ça marche', security: 'Sécurité',
    signIn: 'Se connecter', getStarted: 'Commencer'
  },
  hero: {
    badge: 'Votre Système d\'Exploitation Financier Personnel',
    headline1: 'Votre argent.',
    headline2: 'Votre plan.',
    headline3: 'Votre avenir.',
    sub: 'Une plateforme simple pour comprendre vos finances, développer de meilleures habitudes financières et vous rapprocher de la vie que vous souhaitez.',
    cta1: 'Commencer gratuitement — Sans carte',
    cta2: 'Voir comment fonctionne WealthOS',
    trust: ['Essai gratuit 7 jours', 'Sans carte bancaire', 'Annulez à tout moment'],
    notif: "Bravo ! 🎉 Vous avez économisé 240$ ce mois-ci."
  },
  dashboard: {
    greeting: 'Bonjour, Sarah 👋',
    netWorth: 'Patrimoine net total',
    growth: '↑ +6,42% cette année',
    income: 'Revenus',
    expenses: 'Dépenses',
    savings: 'Épargne',
    savingsRate: 'Taux d\'épargne',
    savedThisMonth: '1 797$ épargnés ce mois-ci',
    recentTx: 'Transactions récentes',
    healthScore: 'Score de santé financière',
    good: '↑ Bon',
    growthChart: 'Croissance du patrimoine net',
    dreamHome: 'Maison de rêve',
    emergencyFund: 'Fonds d\'urgence',
    travelFund: 'Fonds voyage',
    months: ['Jan', 'Fév', 'Mar', 'Avr', 'Mai', 'Jun', 'Jul', 'Aoû', 'Sep', 'Oct', 'Nov', 'Déc'],
    transactions: [
    { name: 'Virement salaire', amount: '+$6 420', color: '#22c55e', icon: '💼' },
    { name: 'Supermarché', amount: '-$124', color: '#f59e0b', icon: '🛒' },
    { name: 'Netflix', amount: '-$15', color: '#ef4444', icon: '📺' },
    { name: 'Épargne auto', amount: '+$640', color: '#22c55e', icon: '🐷' }],

    goals: [
    { label: 'Maison de rêve', pct: 73, color: '#C9A227', amount: '18 400$ / 25 000$' },
    { label: 'Fonds voyage', pct: 48, color: '#0ea5e9', amount: '4 800$ / 10 000$' },
    { label: 'Fonds d\'urgence', pct: 82, color: '#22c55e', amount: '8 200$ / 10 000$' }],

    heroGoals: [
    { label: 'Maison de rêve', pct: 73, color: '#C9A227' },
    { label: 'Fonds d\'urgence', pct: 82, color: '#22c55e' }],

    netWorthLabel: 'Patrimoine net',
    savingsRateLabel: '28% / mois',
    cashFlow: 'Flux de trésorerie',
    cashFlowIncome: 'Revenus : $6 420',
    cashFlowExpenses: 'Dépenses : $3 180',
    wealthosInsight: 'Analyse WealthOS',
    live: 'En direct',
    wealthosDashboard: 'Tableau de bord WealthOS'
  },
  problem: {
    headline: "Gérer son argent ne devrait pas être si compliqué.",
    sub: "Cela vous semble familier ? Vous n\'êtes pas seul.",
    items: [
    { icon: '🏦', title: 'Trop de comptes', desc: 'Votre argent est dispersé entre différentes banques et applications.', color: '#ef4444' },
    { icon: '🔍', title: 'Pas de vue d\'ensemble', desc: "Vous savez ce que vous gagnez, mais pas où va vraiment votre argent.", color: '#f97316' },
    { icon: '🎯', title: 'Des objectifs sans plan', desc: "Épargner pour votre avenir ne devrait pas ressembler à une devinette.", color: '#eab308' }]

  },
  solution: {
    badge: 'La Solution',
    headline: 'Découvrez WealthOS.',
    sub: 'Tout ce dont vous avez besoin pour comprendre, organiser et développer votre vie financière — en un seul endroit.',
    insight: "Vous êtes en bonne voie pour atteindre votre objectif de fonds d'urgence 2 mois plus tôt. 🎉"
  },
  benefits: {
    badge: 'Pourquoi WealthOS',
    headline: 'Conçu autour de votre vie, pas seulement de vos chiffres.',
    items: [
    { title: 'Sachez où vous en êtes.', desc: 'Visualisez votre situation financière complète en quelques secondes.', label: 'Tableau de bord patrimoine', color: '#C9A227' },
    { title: 'Épargnez sans y penser.', desc: 'Définissez votre objectif une fois et laissez WealthOS vous aider à rester sur la bonne voie.', label: 'Moteur d\'épargne automatique', color: '#22c55e' },
    { title: 'Transformez les chiffres en décisions.', desc: 'Obtenez des rapports et des analyses clairs pour prendre de meilleures décisions financières.', label: 'Rapports financiers', color: '#7c3aed' },
    { title: 'Suivez vos progrès.', desc: 'Transformez vos objectifs financiers en jalons visibles.', label: 'Suivi des objectifs', color: '#0ea5e9' }],

    miniLabels: {
      netWorth: 'Patrimoine net',
      autoSavingsGoal: 'Objectif d\'épargne auto',
      housing: 'Logement 45%',
      food: 'Alimentation 20%',
      transport: 'Transport 15%',
      dreamHome: 'Maison de rêve',
      travel: 'Voyage'
    }
  },
  lifestyle: {
    badge: 'Objectifs de vie',
    headline: 'Construisez la vie pour laquelle vous épargnez.',
    sub: "WealthOS ne parle pas de chiffres. Il parle de ce que ces chiffres vous permettent d'accomplir.",
    complete: 'accompli',
    cards: [
    { emoji: '🏡', label: 'Maison de rêve', goal: '18 400$ / 25 000$', pct: 73, img: "https://images.unsplash.com/photo-1684785619392-0bfa21177363", alt: 'Belle maison familiale moderne avec pelouse soignée et ciel bleu' },
    { emoji: '✈️', label: 'Voyager dans le monde', goal: '6 200$ / 10 000$', pct: 62, img: "https://images.unsplash.com/photo-1662501411489-e5394c5fa2f0", alt: 'Couple explorant une belle rue européenne à l\'heure dorée' },
    { emoji: '👨‍👩‍👧', label: 'Avenir familial', goal: '26 450$ / 40 000$', pct: 71, img: "https://img.rocket.new/generatedImages/rocket_gen_img_1da7b19fe-1772690671456.png", alt: 'Famille heureuse de quatre personnes riant ensemble dans un parc ensoleillé' },
    { emoji: '🌅', label: 'Liberté financière', goal: '62 000$ / 100 000$', pct: 62, img: "https://images.unsplash.com/photo-1689164817640-44afb91c74c1", alt: 'Personne debout triomphalement au sommet d\'une montagne au lever du soleil' }]

  },
  beforeAfter: {
    headline: 'Arrêtez de gérer votre argent partout.',
    sub: 'Une plateforme. Une clarté totale.',
    before: { title: 'Avant WealthOS', items: ['Plusieurs applications bancaires', 'Tableurs et notes', 'Calculs manuels', 'Pas de vue d\'ensemble', 'Devinettes constantes'] },
    after: { title: 'Avec WealthOS', items: ['Tout en un seul endroit', 'Situation financière claire', 'Épargne automatique', 'Analyses intelligentes', 'Objectifs financiers'] }
  },
  howItWorks: {
    badge: 'Comment ça marche',
    headline: 'Opérationnel en quelques minutes.',
    steps: [
    { num: '01', title: 'Connectez', desc: 'Connectez vos comptes financiers et actifs en toute sécurité en quelques secondes.' },
    { num: '02', title: 'Comprenez', desc: 'Visualisez votre situation financière complète — patrimoine net, revenus, objectifs.' },
    { num: '03', title: 'Progressez', desc: 'Créez des objectifs, suivez vos progrès et développez de meilleures habitudes financières.' }]

  },
  calculator: {
    badge: 'Calculateur d\'épargne',
    headline: 'Que pourrait devenir votre épargne ?',
    sub: 'De petites décisions aujourd\'hui peuvent créer des progrès significatifs demain.',
    labelMonthly: 'Épargne mensuelle ($)',
    labelYears: 'Années',
    labelRate: 'Croissance annuelle attendue (%)',
    result: 'Vous pourriez avoir',
    yearsLabel: (y: number) => `en ${y} an${y !== 1 ? 's' : ''}`,
    growthLabel: (r: number) => `à ${r}% de croissance annuelle`,
    cta: 'Construire mon plan financier →'
  },
  testimonials: {
    badge: 'Témoignages',
    headline: 'Approuvé par des personnes qui construisent leur avenir financier.',
    items: [
    { name: 'Sarah M.', role: 'Responsable marketing', stars: 5, quote: 'Pour la première fois, je comprends vraiment où va mon argent. WealthOS a tout rendu si clair.', img: "https://img.rocket.new/generatedImages/rocket_gen_img_18dcf7862-1772562312913.png", imgAlt: 'Jeune femme professionnelle souriante' },
    { name: 'James T.', role: 'Entrepreneur', stars: 5, quote: 'J\'ai enfin un endroit pour voir toute ma situation financière. Ça a changé ma façon de penser à l\'argent.', img: "https://images.unsplash.com/photo-1653855394894-4c941ebd3748", imgAlt: 'Jeune homme en tenue décontractée souriant' },
    { name: 'Emily R.', role: 'Enseignante', stars: 5, quote: 'Voir mes progrès a rendu l\'épargne accessible. J\'ai atteint mon objectif de fonds d\'urgence en 8 mois.', img: "https://img.rocket.new/generatedImages/rocket_gen_img_1e3dcc45f-1772203448327.png", imgAlt: 'Femme professionnelle avec un sourire confiant' },
    { name: 'Ahmed R.', role: 'Analyste financier', stars: 5, quote: "Le tableau de bord du patrimoine net offre une clarté que les tableurs n'ont jamais pu donner. Je le recommande à tout le monde.", img: "https://img.rocket.new/generatedImages/rocket_gen_img_117063bc9-1772200771519.png", imgAlt: 'Homme professionnel en tenue décontractée' }]

  },
  security: {
    badge: 'Sécurité',
    headline: 'Vos données financières méritent une protection sérieuse.',
    items: [
    { icon: Lock, title: 'Chiffrement AES-256', desc: 'Toutes les données chiffrées au repos et en transit', color: '#22c55e' },
    { icon: Globe, title: 'Multi-devises', desc: 'USD, EUR et DZD avec conversion en direct', color: '#C9A227' },
    { icon: Activity, title: '99,9% de disponibilité', desc: 'Infrastructure fiable sur laquelle vous pouvez compter', color: '#0ea5e9' },
    { icon: Shield, title: 'Infrastructure sécurisée', desc: 'Zéro vente de données, jamais', color: '#22c55e' }]

  },
  faq: {
    badge: 'FAQ',
    headline: 'Questions fréquentes.',
    items: [
    { q: 'Mes données financières sont-elles sécurisées ?', a: 'Oui. Toutes les données sont chiffrées au repos et en transit avec AES-256 et TLS 1.3. Nous ne vendons jamais vos données à des tiers.' },
    { q: 'Puis-je annuler mon abonnement à tout moment ?', a: 'Absolument. Annulez à tout moment depuis vos paramètres — sans questions. Vos données restent accessibles avec le plan gratuit.' },
    { q: "Qu\'inclut l\'essai gratuit de 7 jours ?", a: 'Accès complet à Pro ou Pro Max — toutes les fonctionnalités débloquées, sans carte bancaire. Après 7 jours, choisissez de vous abonner ou de passer au plan gratuit.' },
    { q: 'Quelles devises sont prises en charge ?', a: 'WealthOS prend en charge USD, EUR et DZD (dinar algérien) avec conversion des taux de change en direct sur tous les actifs et rapports.' },
    { q: 'Puis-je exporter mes données ?', a: 'Les utilisateurs Pro peuvent exporter des rapports PDF. Les utilisateurs Pro Max bénéficient des exports PDF, Excel et JSON — parfait pour les comptables ou les déclarations fiscales.' },
    { q: 'Y a-t-il une application mobile ?', a: 'WealthOS est une application web entièrement responsive qui fonctionne parfaitement sur n\'importe quel appareil. Une application mobile dédiée est dans notre feuille de route.' }]

  },
  finalCta: {
    headline: "Votre avenir ne se construira pas tout seul.",
    sub: 'Commencez à prendre de meilleures décisions financières dès aujourd\'hui.',
    cta1: 'Créer mon compte gratuit →',
    cta2: 'Se connecter',
    trust: ['Sans carte bancaire', 'Essai gratuit 7 jours', 'Annulez à tout moment']
  },
  footer: { rights: 'Tous droits réservés.', privacy: 'Confidentialité', terms: 'Conditions', support: 'Support', cookies: 'Cookies', refund: 'Remboursement' },
  stickyCta: 'Commencer gratuitement →',
  stats: [
  { value: 10, prefix: '', suffix: 'K+', label: 'Utilisateurs actifs' },
  { value: 2.4, prefix: '$', suffix: 'B', label: 'Actifs suivis' },
  { value: 98, prefix: '', suffix: '%', label: 'Taux de satisfaction' },
  { value: 4.9, prefix: '', suffix: '★', label: 'Note de l\'app' }]

};

const TRANSLATIONS = { en: t_en, ar: t_ar, fr: t_fr };

/* ─── Language Switcher ─────────────────────────────────────── */
function LangSwitcher({ lang, setLang }: {lang: Lang;setLang: (l: Lang) => void;}) {
  const labels: Record<Lang, string> = { en: 'EN', ar: 'AR', fr: 'FR' };
  const ariaLabels: Record<Lang, string> = { en: 'Switch to English', ar: 'Switch to Arabic', fr: 'Switch to French' };
  return (
    <div className="flex items-center gap-1 p-1 rounded-xl border border-border bg-muted/40">
      {(['en', 'ar', 'fr'] as Lang[]).map((l) =>
      <button
        key={l}
        onClick={() => setLang(l)}
        className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all duration-200 ${
        lang === l ?
        'bg-foreground text-background shadow-sm' :
        'text-muted-foreground hover:text-foreground'}`
        }
        aria-label={ariaLabels[l]}
        aria-pressed={lang === l}>
          {labels[l]}
        </button>
      )}
    </div>);
}

/* ─── FAQ Item ──────────────────────────────────────────────── */
function FAQItem({ q, a, isRtl }: {q: string;a: string;isRtl?: boolean;}) {
  const [open, setOpen] = useState(false);
  return (
    <div
      className="border border-border/60 rounded-2xl overflow-hidden cursor-pointer hover:border-primary/40 transition-all duration-300 group"
      onClick={() => setOpen(!open)}>
      <div className={`flex items-center justify-between px-6 py-5 gap-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
        <p className="text-sm font-semibold text-foreground group-hover:text-primary transition-colors">{q}</p>
        <ChevronDown size={16} className={`text-muted-foreground flex-shrink-0 transition-transform duration-300 ${open ? 'rotate-180 text-primary' : ''}`} />
      </div>
      <div className={`overflow-hidden transition-all duration-400 ease-in-out ${open ? 'max-h-48' : 'max-h-0'}`}>
        <div className="px-6 pb-5 border-t border-border/40">
          <p className="text-sm text-muted-foreground leading-relaxed pt-4">{a}</p>
        </div>
      </div>
    </div>);

}

/* ─── Animated Counter ──────────────────────────────────────── */
function AnimatedNumber({ target, prefix = '', suffix = '', duration = 1500, decimals = 0

}: {target: number;prefix?: string;suffix?: string;duration?: number;decimals?: number;}) {
  const [current, setCurrent] = useState(0);
  const [started, setStarted] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started) setStarted(true);
    }, { threshold: 0.5 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const steps = 60;
    const increment = target / steps;
    let count = 0;
    const timer = setInterval(() => {
      count += increment;
      if (count >= target) {setCurrent(target);clearInterval(timer);} else
      setCurrent(parseFloat(count.toFixed(decimals)));
    }, duration / steps);
    return () => clearInterval(timer);
  }, [started, target, duration, decimals]);

  return <span ref={ref}>{prefix}{decimals > 0 ? current.toFixed(decimals) : current.toLocaleString()}{suffix}</span>;
}

/* ─── Scroll Reveal ─────────────────────────────────────────── */
function ScrollReveal({ children, delay = 0, className = '', direction = 'up'

}: {children: React.ReactNode;delay?: number;className?: string;direction?: 'up' | 'left' | 'right' | 'scale';}) {
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting) {setVisible(true);observer.disconnect();}
    }, { threshold: 0.08 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);

  const getTransform = () => {
    if (visible) return 'translateY(0) translateX(0) scale(1)';
    if (direction === 'left') return 'translateX(-32px)';
    if (direction === 'right') return 'translateX(32px)';
    if (direction === 'scale') return 'scale(0.92)';
    return 'translateY(28px)';
  };

  return (
    <div
      ref={ref}
      className={`transition-all duration-700 ease-out ${className}`}
      style={{
        opacity: visible ? 1 : 0,
        transform: getTransform(),
        transitionDelay: `${delay}ms`
      }}>
      {children}
    </div>);

}

/* ─── Parallax Image ────────────────────────────────────────── */
function ParallaxImage({ src, alt, className = '' }: {src: string;alt: string;className?: string;}) {
  const [offset, setOffset] = useState(0);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const handleScroll = () => {
      if (!ref.current) return;
      const rect = ref.current.getBoundingClientRect();
      const center = rect.top + rect.height / 2;
      const viewCenter = window.innerHeight / 2;
      setOffset((center - viewCenter) * 0.12);
    };
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div ref={ref} className={`overflow-hidden ${className}`}>
      <img
        src={src}
        alt={alt}
        className="w-full h-full object-cover transition-transform duration-100 ease-linear will-change-transform"
        style={{ transform: `translateY(${offset}px) scale(1.12)` }} />
    </div>);
}

/* ─── Section Divider ───────────────────────────────────────── */
function SectionDivider() {
  return (
    <div className="relative flex items-center justify-center py-2 overflow-hidden">
      <div className="absolute inset-x-0 h-px bg-gradient-to-r from-transparent via-primary/30 to-transparent" />
      <div className="relative w-2 h-2 rounded-full bg-primary/60 shadow-[0_0_8px_2px_rgba(201,162,39,0.4)]" />
    </div>);

}

/* ─── Hero Dashboard Widget ─────────────────────────────────── */
function HeroDashboard({ notifText, db }: {notifText: string;db: typeof t_en.dashboard;}) {
  const [animStep, setAnimStep] = useState(0);
  const [netWorth, setNetWorth] = useState(0);
  const [showNotif, setShowNotif] = useState(false);
  const [goalProgress, setGoalProgress] = useState(0);
  const bars = [30, 45, 38, 55, 48, 65, 72, 68, 80, 88, 76, 95];

  useEffect(() => {
    const timer = setInterval(() => setAnimStep((s) => (s + 1) % 5), 1000);
    return () => clearInterval(timer);
  }, []);

  useEffect(() => {
    const target = 42680;
    const steps = 50;
    const inc = target / steps;
    let count = 0;
    const t = setInterval(() => {
      count += inc;
      if (count >= target) {setNetWorth(target);clearInterval(t);} else
      setNetWorth(Math.floor(count));
    }, 35);
    return () => clearInterval(t);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => setShowNotif(true), 2200);
    return () => clearTimeout(t);
  }, []);

  useEffect(() => {
    const t = setTimeout(() => {
      let p = 0;
      const interval = setInterval(() => {
        p += 2;
        setGoalProgress(Math.min(p, 73));
        if (p >= 73) clearInterval(interval);
      }, 20);
      return () => clearInterval(interval);
    }, 600);
    return () => clearTimeout(t);
  }, []);

  return (
    <div className="relative">
      <div className="absolute inset-0 rounded-3xl bg-primary/8 blur-3xl scale-110 pointer-events-none" />
      <div className="relative glass-card rounded-3xl border border-primary/30 p-5 shadow-2xl backdrop-blur-xl">
        <div className="flex items-center justify-between mb-4">
          <div>
            <p className="text-[10px] text-muted-foreground">WealthOS</p>
            <p className="text-xs font-bold text-foreground">{db.greeting}</p>
          </div>
          <div className="w-7 h-7 rounded-full bg-primary/20 flex items-center justify-center ring-2 ring-primary/30">
            <span className="text-[10px] font-bold text-primary">S</span>
          </div>
        </div>
        <div className="rounded-2xl bg-gradient-to-br from-primary/20 to-primary/5 border border-primary/20 p-4 mb-3">
          <p className="text-[10px] text-muted-foreground mb-1">{db.netWorth}</p>
          <p className="text-2xl font-bold text-foreground">${netWorth.toLocaleString()}</p>
          <p className="text-[10px] text-[#22c55e] mt-1">{db.growth}</p>
        </div>
        <div className="grid grid-cols-3 gap-2 mb-3">
          {[
          { label: db.income, value: '$6,420', color: '#22c55e' },
          { label: db.expenses, value: '$3,180', color: '#f59e0b' },
          { label: db.savings, value: '28%', color: '#C9A227' }].
          map((item) =>
          <div key={item.label} className="rounded-xl bg-muted/40 p-2.5 text-center">
              <p className="text-[9px] text-muted-foreground mb-0.5">{item.label}</p>
              <p className="text-xs font-bold" style={{ color: item.color }}>{item.value}</p>
            </div>
          )}
        </div>
        {/* Animated chart */}
        <div className="rounded-xl bg-muted/30 p-3 mb-3">
          <p className="text-[9px] text-muted-foreground mb-2">{db.growthChart}</p>
          <div className="flex items-end gap-0.5 h-10">
            {bars.map((h, i) =>
            <div
              key={i}
              className="flex-1 rounded-sm transition-all duration-700"
              style={{
                height: `${h}%`,
                backgroundColor: i <= animStep * 2 + 2 ? '#C9A227' : 'rgba(201,162,39,0.12)',
                transitionDelay: `${i * 30}ms`
              }} />

            )}
          </div>
        </div>
        {/* Animated goal progress */}
        <div className="space-y-2">
          {db.heroGoals.map((g) =>
          <div key={g.label}>
              <div className="flex justify-between text-[9px] text-muted-foreground mb-1">
                <span>{g.label}</span><span style={{ color: g.color }}>{g.pct}%</span>
              </div>
              <div className="h-1.5 rounded-full bg-border/50 overflow-hidden">
                <div
                className="h-full rounded-full transition-all duration-1200 ease-out"
                style={{ width: `${goalProgress}%`, backgroundColor: g.color }} />
              </div>
            </div>
          )}
        </div>
        {/* Notification */}
        <div className={`mt-3 rounded-xl bg-[#22c55e]/10 border border-[#22c55e]/20 px-3 py-2 flex items-center gap-2 transition-all duration-700 ${showNotif ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-3'}`}>
          <Bell size={10} className="text-[#22c55e] flex-shrink-0 animate-pulse" />
          <p className="text-[10px] text-[#22c55e] font-medium">{notifText}</p>
        </div>
      </div>
      {/* Floating widgets */}
      <div className={`absolute -top-4 -right-4 glass-card rounded-2xl border border-primary/30 px-3 py-2 flex items-center gap-2 shadow-lg transition-all duration-700 backdrop-blur-xl ${animStep >= 1 ? 'opacity-100 translate-y-0' : 'opacity-0 -translate-y-2'}`}>
        <TrendingUp size={12} className="text-[#22c55e]" />
        <div>
          <p className="text-[9px] text-muted-foreground">{db.netWorthLabel}</p>
          <p className="text-xs font-bold text-[#22c55e]">↑ +12.4%</p>
        </div>
      </div>
      <div className={`absolute -bottom-3 -right-6 glass-card rounded-2xl border border-primary/20 px-3 py-2 shadow-lg transition-all duration-700 backdrop-blur-xl ${animStep >= 2 ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-2'}`}>
        <p className="text-[9px] text-muted-foreground">{db.savingsRate}</p>
        <p className="text-xs font-bold text-primary">{db.savingsRateLabel}</p>
      </div>
    </div>);

}

/* ─── Large Dashboard Preview ───────────────────────────────── */
function LargeDashboard({ insightText, db }: {insightText: string;db: typeof t_en.dashboard;}) {
  const [animStep, setAnimStep] = useState(0);
  const [started, setStarted] = useState(false);
  const [goalWidths, setGoalWidths] = useState([0, 0, 0]);
  const ref = useRef<HTMLDivElement>(null);
  const bars = [30, 45, 38, 55, 48, 65, 72, 68, 80, 88, 76, 95];

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !started) setStarted(true);
    }, { threshold: 0.2 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [started]);

  useEffect(() => {
    if (!started) return;
    const timer = setInterval(() => setAnimStep((s) => (s + 1) % 6), 800);
    // Animate goal bars
    const targets = db.goals.map((g) => g.pct);
    targets.forEach((target, idx) => {
      setTimeout(() => {
        let p = 0;
        const interval = setInterval(() => {
          p += 2;
          setGoalWidths((prev) => {
            const next = [...prev];
            next[idx] = Math.min(p, target);
            return next;
          });
          if (p >= target) clearInterval(interval);
        }, 18);
      }, idx * 200 + 400);
    });
    return () => clearInterval(timer);
  }, [started, db.goals]);

  return (
    <div ref={ref} className="relative rounded-3xl overflow-hidden border border-primary/20 bg-gradient-to-br from-muted/50 to-muted/20 p-6 sm:p-8 shadow-2xl">
      <div className="absolute inset-0 bg-gradient-to-br from-primary/3 to-transparent pointer-events-none" />
      <div className="flex items-center justify-between mb-6">
        <div>
          <p className="text-xs text-muted-foreground">{db.wealthosDashboard}</p>
          <p className="text-sm font-bold text-foreground">{db.greeting}</p>
        </div>
        <div className="flex items-center gap-2">
          <div className="w-2 h-2 rounded-full bg-[#22c55e] animate-pulse" />
          <span className="text-xs text-muted-foreground">{db.live}</span>
        </div>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        <div className="lg:col-span-2 space-y-4">
          <div className="glass-card rounded-2xl p-5 border border-border">
            <div className="flex items-start justify-between mb-4">
              <div>
                <p className="text-xs text-muted-foreground mb-1">{db.netWorth}</p>
                <p className="text-3xl font-bold text-foreground">
                  <AnimatedNumber target={42680} prefix="$" />
                </p>
                <p className="text-xs text-[#22c55e] mt-1">{db.growth}</p>
              </div>
              <div className="text-right">
                <p className="text-xs text-muted-foreground mb-1">{db.cashFlow}</p>
                <p className="text-sm font-bold text-[#22c55e]">{db.cashFlowIncome}</p>
                <p className="text-sm font-bold text-muted-foreground">{db.cashFlowExpenses}</p>
              </div>
            </div>
            {/* Animated chart bars */}
            <div className="flex items-end gap-1 h-16">
              {bars.map((h, i) =>
              <div
                key={i}
                className="flex-1 rounded-sm transition-all duration-600"
                style={{
                  height: started ? `${h}%` : '4%',
                  backgroundColor: i <= animStep * 2 ? '#C9A227' : 'rgba(201,162,39,0.15)',
                  transitionDelay: `${i * 40}ms`
                }} />

              )}
            </div>
            <div className="flex justify-between text-[9px] text-muted-foreground mt-1.5">
              {db.months.map((m) => <span key={m}>{m}</span>)}
            </div>
          </div>
          <div className="grid grid-cols-3 gap-3">
            {db.goals.map((g, idx) =>
            <div key={g.label} className="glass-card rounded-2xl p-3 border border-border hover:border-primary/30 transition-all hover:-translate-y-0.5">
                <div className="flex justify-between items-start mb-2">
                  <p className="text-[10px] font-semibold text-foreground leading-tight">{g.label}</p>
                  <span className="text-[10px] font-bold" style={{ color: g.color }}>{g.pct}%</span>
                </div>
                <div className="h-1.5 rounded-full bg-border mb-1.5 overflow-hidden">
                  <div
                  className="h-full rounded-full transition-all duration-1000 ease-out"
                  style={{ width: `${goalWidths[idx]}%`, backgroundColor: g.color }} />
                </div>
                <p className="text-[9px] text-muted-foreground">{g.amount}</p>
              </div>
            )}
          </div>
          <div className="glass-card rounded-2xl p-4 border border-primary/30 bg-primary/5 flex items-start gap-3">
            <div className="w-8 h-8 rounded-xl bg-primary/20 flex items-center justify-center flex-shrink-0">
              <Sparkles size={14} className="text-primary" />
            </div>
            <div>
              <p className="text-xs font-semibold text-foreground mb-0.5">{db.wealthosInsight}</p>
              <p className="text-xs text-muted-foreground">{insightText}</p>
            </div>
          </div>
        </div>
        <div className="space-y-4">
          <div className="glass-card rounded-2xl p-4 border border-[#22c55e]/30 bg-[#22c55e]/5">
            <div className="flex items-center gap-2 mb-2">
              <PiggyBank size={14} className="text-[#22c55e]" />
              <p className="text-xs font-semibold text-foreground">{db.savingsRate}</p>
            </div>
            <p className="text-3xl font-bold text-[#22c55e]">28%</p>
            <p className="text-[10px] text-muted-foreground mt-1">{db.savedThisMonth}</p>
          </div>
          <div className="glass-card rounded-2xl p-4 border border-border">
            <p className="text-xs font-semibold text-foreground mb-3">{db.recentTx}</p>
            <div className="space-y-2.5">
              {db.transactions.map((tx, i) =>
              <div
                key={i}
                className="flex items-center justify-between transition-all duration-500"
                style={{ opacity: started && i <= animStep ? 1 : 0, transform: started && i <= animStep ? 'translateX(0)' : 'translateX(-8px)' }}>
                  <div className="flex items-center gap-2">
                    <span className="text-sm">{tx.icon}</span>
                    <p className="text-[10px] text-muted-foreground">{tx.name}</p>
                  </div>
                  <p className="text-[10px] font-bold" style={{ color: tx.color }}>{tx.amount}</p>
                </div>
              )}
            </div>
          </div>
          <div className="glass-card rounded-2xl p-4 border border-primary/20">
            <p className="text-[10px] text-muted-foreground mb-1">{db.healthScore}</p>
            <div className="flex items-end gap-2">
              <p className="text-3xl font-bold text-primary">82</p>
              <p className="text-xs text-[#22c55e] mb-1">{db.good}</p>
            </div>
            <div className="h-2 rounded-full bg-border mt-2 overflow-hidden">
              <div className="h-full rounded-full bg-gradient-to-r from-primary to-[#22c55e] transition-all duration-1200 ease-out" style={{ width: started ? '82%' : '0%' }} />
            </div>
          </div>
        </div>
      </div>
    </div>);

}

/* ─── Savings Calculator ────────────────────────────────────── */
function SavingsCalculator({ t }: {t: typeof t_en;}) {
  const [monthly, setMonthly] = useState(300);
  const [years, setYears] = useState(5);
  const [rate, setRate] = useState(5);
  const [result, setResult] = useState(0);

  const calculate = useCallback(() => {
    const r = rate / 100 / 12;
    const n = years * 12;
    if (r === 0) {setResult(monthly * n);return;}
    const fv = monthly * ((Math.pow(1 + r, n) - 1) / r);
    setResult(Math.round(fv));
  }, [monthly, years, rate]);

  useEffect(() => {calculate();}, [calculate]);

  const barHeights = Array.from({ length: 12 }, (_, i) => {
    const r = rate / 100 / 12;
    const n = Math.ceil(years / 12 * (i + 1)) * 12;
    if (r === 0) return monthly * n;
    return monthly * ((Math.pow(1 + r, n) - 1) / r);
  });
  const maxBar = Math.max(...barHeights);
  const isRtl = t.dir === 'rtl';

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 sm:gap-8 items-start lg:items-center">
      <div className="space-y-5 sm:space-y-6">
        {[
        { label: t.calculator.labelMonthly, value: monthly, setter: setMonthly, min: 50, max: 5000, step: 50, display: `$${monthly}` },
        { label: t.calculator.labelYears, value: years, setter: setYears, min: 1, max: 30, step: 1, display: `${years}` },
        { label: t.calculator.labelRate, value: rate, setter: setRate, min: 1, max: 20, step: 0.5, display: `${rate}%` }].
        map(({ label, value, setter, min, max, step, display }) =>
        <div key={label}>
            <div className={`flex justify-between mb-2 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <label className="text-sm font-medium text-foreground">{label}</label>
              <span className="text-sm font-bold text-primary">{display}</span>
            </div>
            <input
            type="range" min={min} max={max} step={step} value={value}
            onChange={(e) => setter(Number(e.target.value))}
            className="w-full h-2 rounded-full appearance-none cursor-pointer touch-target"
            aria-label={label}
            style={{ background: `linear-gradient(to right, #C9A227 0%, #C9A227 ${(value - min) / (max - min) * 100}%, rgba(255,255,255,0.1) ${(value - min) / (max - min) * 100}%, rgba(255,255,255,0.1) 100%)` }} />
          
          </div>
        )}
      </div>
      <div className="glass-card rounded-3xl p-6 sm:p-8 border border-primary/30 text-center relative overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none" />
        <p className="text-sm text-muted-foreground mb-2">{t.calculator.result}</p>
        <p className="calc-result-value text-4xl sm:text-5xl font-bold text-primary mb-1">${result.toLocaleString()}</p>
        <p className="text-xs text-muted-foreground mb-4">{t.calculator.yearsLabel(years)} {t.calculator.growthLabel(rate)}</p>
        <div className="flex items-end gap-1 h-10 sm:h-12 mb-4 sm:mb-5 px-2">
          {barHeights.map((h, i) =>
          <div
            key={i}
            className="flex-1 rounded-sm transition-all duration-500"
            style={{ height: `${h / maxBar * 100}%`, backgroundColor: `rgba(201,162,39,${0.3 + i / barHeights.length * 0.7})` }} />
          )}
        </div>
        <p className="text-xs text-muted-foreground italic mb-4 sm:mb-5">{t.calculator.sub}</p>
        <Link
          href="/register"
          className="inline-flex items-center justify-center gap-2 w-full sm:w-auto px-6 py-3 rounded-xl gold-gradient text-primary-foreground font-bold text-sm hover:opacity-90 transition-all hover:-translate-y-0.5 active:scale-95 shadow-lg shadow-primary/25 touch-target">
          {t.calculator.cta}
        </Link>
      </div>
    </div>);

}

/* ─── Testimonial Carousel ──────────────────────────────────── */
function TestimonialCarousel({ items }: {items: typeof t_en.testimonials.items;}) {
  const [active, setActive] = useState(0);
  const timerRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const resetTimer = useCallback(() => {
    if (timerRef.current) clearInterval(timerRef.current);
    timerRef.current = setInterval(() => setActive((a) => (a + 1) % items.length), 4000);
  }, [items.length]);

  useEffect(() => {
    resetTimer();
    return () => {if (timerRef.current) clearInterval(timerRef.current);};
  }, [resetTimer]);

  const prev = () => {setActive((a) => (a - 1 + items.length) % items.length);resetTimer();};
  const next = () => {setActive((a) => (a + 1) % items.length);resetTimer();};

  return (
    <div className="relative">
      <div className="hidden lg:grid grid-cols-4 gap-5">
        {items.map((item, i) =>
        <div key={i} className="glass-card rounded-3xl p-6 border border-border hover:border-primary/30 transition-all duration-300 hover:-translate-y-2 hover:shadow-xl hover:shadow-primary/10 flex flex-col group">
            <div className="flex gap-0.5 mb-4">
              {Array.from({ length: item.stars }).map((_, si) =>
            <Star key={si} size={12} className="text-primary fill-primary" />
            )}
            </div>
            <p className="text-sm text-foreground leading-relaxed mb-5 italic flex-1">&ldquo;{item.quote}&rdquo;</p>
            <div className="flex items-center gap-3">
              <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border-2 border-border group-hover:border-primary/40 transition-colors">
                {item.img ? <img src={item.img} alt={item.imgAlt} className="w-full h-full object-cover" /> : <span className="w-full h-full flex items-center justify-center text-sm font-bold bg-muted text-foreground">{item.name.charAt(0)}</span>}
              </div>
              <div>
                <p className="text-sm font-semibold text-foreground">{item.name}</p>
                <p className="text-xs text-muted-foreground">{item.role}</p>
              </div>
            </div>
          </div>
        )}
      </div>
      <div className="lg:hidden">
        <div className="overflow-hidden rounded-3xl">
          <div className="flex transition-transform duration-500 ease-in-out" style={{ transform: `translateX(-${active * 100}%)` }}>
            {items.map((item, i) =>
            <div key={i} className="w-full flex-shrink-0 glass-card rounded-3xl p-6 border border-border flex flex-col">
                <div className="flex gap-0.5 mb-4">
                  {Array.from({ length: item.stars }).map((_, si) =>
                <Star key={si} size={12} className="text-primary fill-primary" />
                )}
                </div>
                <p className="text-sm text-foreground leading-relaxed mb-5 italic flex-1">&ldquo;{item.quote}&rdquo;</p>
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full overflow-hidden flex-shrink-0 border-2 border-border">
                    {item.img ? <img src={item.img} alt={item.imgAlt} className="w-full h-full object-cover" /> : <span className="w-full h-full flex items-center justify-center text-sm font-bold bg-muted text-foreground">{item.name.charAt(0)}</span>}
                  </div>
                  <div>
                    <p className="text-sm font-semibold text-foreground">{item.name}</p>
                    <p className="text-xs text-muted-foreground">{item.role}</p>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
        <div className="flex items-center justify-center gap-3 mt-4">
          <button onClick={prev} className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:border-primary/50 transition-colors">
            <ChevronLeft size={14} className="text-muted-foreground" />
          </button>
          {items.map((_, i) =>
          <button key={i} onClick={() => {setActive(i);resetTimer();}} className={`w-2 h-2 rounded-full transition-all ${i === active ? 'bg-primary w-4' : 'bg-border'}`} />
          )}
          <button onClick={next} className="w-8 h-8 rounded-full border border-border flex items-center justify-center hover:border-primary/50 transition-colors">
            <ChevronRight size={14} className="text-muted-foreground" />
          </button>
        </div>
      </div>
    </div>);

}

/* ─── Sticky CTA ────────────────────────────────────────────── */
function StickyCTA({ label }: {label: string;}) {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const handleScroll = () => setVisible(window.scrollY > 600);
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  return (
    <div
      className={`fixed z-50 transition-all duration-500 ${visible ? 'opacity-100 translate-y-0' : 'opacity-0 translate-y-4 pointer-events-none'}`}
      style={{
        bottom: 'calc(4.5rem + env(safe-area-inset-bottom, 0px))',
        right: 'max(1.5rem, env(safe-area-inset-right, 1.5rem))'
      }}>
      
      <Link
        href="/register"
        className="flex items-center gap-2 px-4 py-2.5 sm:px-5 sm:py-3 rounded-2xl gold-gradient text-primary-foreground font-bold text-xs sm:text-sm shadow-2xl shadow-primary/40 hover:opacity-90 hover:-translate-y-1 transition-all active:scale-95 relative overflow-hidden group touch-target">
        <span className="absolute inset-0 w-1/2 bg-gradient-to-r from-transparent via-white/20 to-transparent -translate-x-full group-hover:translate-x-full transition-transform duration-700 pointer-events-none" />
        {label} <ArrowRight size={14} />
      </Link>
    </div>);

}

/* ─── Lifestyle Goal Card ───────────────────────────────────── */
function LifestyleGoalCard({ card, complete, delay



}: {card: {emoji: string;label: string;goal: string;pct: number;img: string;alt: string;};complete: string;delay: number;}) {
  const [progressWidth, setProgressWidth] = useState(0);
  const [visible, setVisible] = useState(false);
  const ref = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(([entry]) => {
      if (entry.isIntersecting && !visible) {
        setVisible(true);
        setTimeout(() => {
          let p = 0;
          const interval = setInterval(() => {
            p += 1.5;
            setProgressWidth(Math.min(p, card.pct));
            if (p >= card.pct) clearInterval(interval);
          }, 16);
        }, delay + 200);
      }
    }, { threshold: 0.2 });
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [visible, card.pct, delay]);

  return (
    <div
      ref={ref}
      className="group relative rounded-3xl overflow-hidden border border-border hover:border-primary/50 transition-all duration-400 hover:-translate-y-3 cursor-pointer shadow-lg hover:shadow-2xl hover:shadow-primary/15"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(32px)',
        transition: `all 0.7s ease-out ${delay}ms`
      }}>
      {/* Large lifestyle image with parallax */}
      <div className="aspect-[3/4] relative overflow-hidden">
        <img
          src={card.img}
          alt={card.alt}
          className="w-full h-full object-cover group-hover:scale-110 transition-transform duration-700 ease-out" />
        {/* Gradient overlay */}
        <div className="absolute inset-0 bg-gradient-to-t from-background via-background/60 to-transparent" />
        {/* Top badge */}
        <div className="absolute top-4 left-4 right-4 flex items-center justify-between">
          <div className="flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-background/80 backdrop-blur-sm border border-border/60">
            <span className="text-sm">{card.emoji}</span>
          </div>
          <div className="px-2.5 py-1 rounded-full bg-primary/90 backdrop-blur-sm">
            <span className="text-[10px] font-bold text-primary-foreground">{card.pct}%</span>
          </div>
        </div>
      </div>
      {/* Progress overlay at bottom */}
      <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-background/95 to-transparent">
        <p className="text-sm font-bold text-foreground mb-1">{card.label}</p>
        <p className="text-xs text-muted-foreground mb-3">{card.goal}</p>
        {/* Animated progress bar */}
        <div className="h-2 rounded-full bg-border/50 mb-2 overflow-hidden">
          <div
            className="h-full rounded-full bg-gradient-to-r from-primary to-primary/70 transition-none"
            style={{ width: `${progressWidth}%` }} />
        </div>
        <div className="flex justify-between items-center">
          <p className="text-[10px] text-primary font-semibold">{card.pct}% {complete}</p>
          <div className="w-5 h-5 rounded-full bg-primary/20 flex items-center justify-center group-hover:bg-primary/40 transition-colors">
            <TrendingUp size={10} className="text-primary" />
          </div>
        </div>
      </div>
    </div>);
}

/* ─── Main Page ─────────────────────────────────────────────── */
export default function WelcomePage() {
  const [lang, setLang] = useState<Lang>('en');
  const [isDark, setIsDark] = useState(false);
  const [mounted, setMounted] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);

  // CMS content from Supabase (published content overrides hardcoded defaults)
  const cmsLang = lang as CmsLang;
  const { testimonials: cmsTestimonials, faqItems: cmsFaqItems, getText } = useCmsContent(cmsLang);

  // Load saved preferences on mount
  useEffect(() => {
    setMounted(true);
    const savedLang = localStorage.getItem('wealthos_lang') as Lang | null;
    if (savedLang && (savedLang === 'en' || savedLang === 'ar' || savedLang === 'fr')) {
      setLang(savedLang);
    }
    const savedTheme = localStorage.getItem('wealthos_welcome_theme');
    setIsDark(savedTheme === 'dark');
  }, []);

  // Apply dir and font to document when language changes
  useEffect(() => {
    if (!mounted) return;
    const t = TRANSLATIONS[lang];
    document.documentElement.dir = t.dir;
    document.documentElement.lang = lang;
    if (lang === 'ar') {
      document.documentElement.classList.add('font-arabic');
    } else {
      document.documentElement.classList.remove('font-arabic');
    }
    localStorage.setItem('wealthos_lang', lang);
  }, [lang, mounted]);

  // Prevent body scroll when mobile menu is open
  useEffect(() => {
    if (mobileMenuOpen) {
      document.body.classList.add('welcome-menu-open');
    } else {
      document.body.classList.remove('welcome-menu-open');
    }
    return () => document.body.classList.remove('welcome-menu-open');
  }, [mobileMenuOpen]);

  const handleSetLang = (l: Lang) => {
    setLang(l);
  };

  const t = TRANSLATIONS[lang];
  const isRtl = t.dir === 'rtl';

  const toggleTheme = () => {
    const next = !isDark;
    setIsDark(next);
    localStorage.setItem('wealthos_welcome_theme', next ? 'dark' : 'light');
  };

  return (
    <div
      className={`min-h-screen bg-background overflow-x-hidden transition-all duration-300 ${isDark ? 'dark' : 'light'} ${isRtl ? 'font-arabic' : ''}`}
      dir={t.dir}
      style={isRtl ? { fontFamily: "'Cairo', 'Tajawal', sans-serif" } : {}}>
      
      {/* Arabic font import */}
      {isRtl &&
      <style>{`
          @import url('https://fonts.googleapis.com/css2?family=Cairo:wght@400;500;600;700;800&display=swap');
          .font-arabic, .font-arabic * { font-family: 'Cairo', 'Tajawal', sans-serif !important; }
        `}</style>
      }

      {/* Global micro-interaction styles */}
      <style>{`
        @keyframes cta-shimmer {
          0% { transform: translateX(-100%); }
          100% { transform: translateX(300%); }
        }
        @keyframes float-widget {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-6px); }
        }
        @keyframes glow-pulse {
          0%, 100% { box-shadow: 0 0 20px 4px rgba(201,162,39,0.25); }
          50% { box-shadow: 0 0 32px 8px rgba(201,162,39,0.45); }
        }
        @keyframes border-glow {
          0%, 100% { border-color: rgba(201,162,39,0.3); }
          50% { border-color: rgba(201,162,39,0.7); }
        }
        .animate-cta-shimmer { animation: cta-shimmer 2.5s ease-in-out infinite; }
        .animate-float { animation: float-widget 4s ease-in-out infinite; }
        .animate-glow-pulse { animation: glow-pulse 2.5s ease-in-out infinite; }
        .btn-gold-glow:hover { box-shadow: 0 0 24px 6px rgba(201,162,39,0.4), 0 4px 20px rgba(201,162,39,0.3); }
        .card-hover-lift { transition: transform 0.3s ease, box-shadow 0.3s ease, border-color 0.3s ease; }
        .card-hover-lift:hover { transform: translateY(-6px); box-shadow: 0 20px 40px rgba(0,0,0,0.3), 0 0 0 1px rgba(201,162,39,0.2); }
        .progress-bar-fill { transition: width 1.2s cubic-bezier(0.4, 0, 0.2, 1); }

        /* ── Responsive nav mobile menu ── */
        .welcome-mobile-menu {
          position: fixed;
          inset: 0;
          z-index: 100;
          display: flex;
        }
        .welcome-mobile-menu-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0,0,0,0.65);
          backdrop-filter: blur(4px);
          -webkit-backdrop-filter: blur(4px);
        }
        .welcome-mobile-menu-drawer {
          position: relative;
          width: min(300px, 85vw);
          height: 100%;
          background: var(--glass-bg);
          backdrop-filter: blur(24px);
          -webkit-backdrop-filter: blur(24px);
          border-right: 1px solid var(--glass-border);
          display: flex;
          flex-direction: column;
          overflow-y: auto;
          padding: 1.5rem 1rem;
          padding-top: max(1.5rem, env(safe-area-inset-top));
          padding-bottom: max(1.5rem, env(safe-area-inset-bottom));
          animation: drawerSlideIn 0.3s cubic-bezier(0.22,1,0.36,1);
        }
        [dir="rtl"] .welcome-mobile-menu-drawer {
          border-right: none;
          border-left: 1px solid var(--glass-border);
          margin-left: auto;
          animation: drawerSlideInRtl 0.3s cubic-bezier(0.22,1,0.36,1);
        }
        @keyframes drawerSlideIn {
          from { transform: translateX(-100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }
        @keyframes drawerSlideInRtl {
          from { transform: translateX(100%); opacity: 0; }
          to { transform: translateX(0); opacity: 1; }
        }

        /* ── Prevent body scroll when menu open ── */
        body.welcome-menu-open { overflow: hidden; }

        /* ── Fluid hero heading ── */
        .hero-heading {
          font-size: clamp(2rem, 7vw, 3.5rem);
          line-height: 1.1;
        }
        .section-heading {
          font-size: clamp(1.5rem, 5vw, 2.5rem);
          line-height: 1.2;
        }
        .section-sub {
          font-size: clamp(0.875rem, 2.5vw, 1.125rem);
        }

        /* ── Responsive hero dashboard ── */
        @media (max-width: 639px) {
          .hero-dashboard-wrapper {
            position: relative !important;
            bottom: auto !important;
            left: auto !important;
            right: auto !important;
            width: 100% !important;
            margin-top: 1.5rem;
          }
          .hero-image-overlay-widget {
            display: none;
          }
        }

        /* ── Responsive calculator ── */
        @media (max-width: 767px) {
          .calc-result-value {
            font-size: clamp(2rem, 10vw, 3rem);
          }
        }

        /* ── Landscape safe ── */
        @media (orientation: landscape) and (max-height: 500px) {
          .hero-section-landscape {
            padding-top: 4rem;
            padding-bottom: 2rem;
          }
        }

        /* ── Ultrawide constraint ── */
        @media (min-width: 2560px) {
          .ultrawide-max {
            max-width: 1800px;
            margin-left: auto;
            margin-right: auto;
          }
        }

        /* ── Focus visible ── */
        *:focus-visible {
          outline: 2px solid var(--primary);
          outline-offset: 2px;
        }
      `}</style>

      {/* Ambient background */}
      <div className="fixed inset-0 pointer-events-none z-0">
        <div className="absolute top-0 left-1/4 w-[600px] h-[600px] rounded-full bg-primary/4 blur-[140px]" />
        <div className="absolute top-1/3 right-0 w-[500px] h-[500px] rounded-full bg-[#22c55e]/3 blur-[120px]" />
        <div className="absolute bottom-0 left-0 w-[400px] h-[400px] rounded-full bg-primary/3 blur-[100px]" />
      </div>

      <StickyCTA label={t.stickyCta} />
      <UrgencyBanner lang={lang} isRtl={isRtl} />
      <ActivityNotification lang={lang} isRtl={isRtl} />

      <div className="relative z-10">
        {/* ── Skip to content ── */}
        <a href="#main-content" className="skip-link">Skip to content</a>

        {/* ── Mobile Menu ── */}
        {mobileMenuOpen &&
        <div className="welcome-mobile-menu" role="dialog" aria-modal="true" aria-label="Navigation menu">
            <div className="welcome-mobile-menu-overlay" onClick={() => setMobileMenuOpen(false)} />
            <div className="welcome-mobile-menu-drawer">
              {/* Header */}
              <div className={`flex items-center justify-between mb-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
                <div className="flex items-center gap-2">
                  <AppLogo size={28} />
                  <span className="text-sm font-bold text-foreground">Wealth<span className="text-primary">OS</span></span>
                </div>
                <button
                onClick={() => setMobileMenuOpen(false)}
                className="w-9 h-9 rounded-lg bg-muted/60 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors touch-target"
                aria-label="Close menu">
                
                  <X size={18} />
                </button>
              </div>
              {/* Nav links */}
              <nav className="flex flex-col gap-1 mb-6">
                {[['#features', t.nav.features], ['#how-it-works', t.nav.howItWorks], ['#pricing', t.nav.pricing], ['#testimonials', t.nav.reviews], ['#faq', t.nav.faq]].map(([href, label]) =>
              <a
                key={href}
                href={href}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl text-sm font-medium text-muted-foreground hover:text-foreground hover:bg-muted/60 transition-all touch-target ${isRtl ? 'flex-row-reverse text-right' : ''}`}>
                
                    {label}
                  </a>
              )}
              </nav>
              {/* Divider */}
              <div className="h-px bg-border/50 mb-6" />
              {/* CTA buttons */}
              <div className="flex flex-col gap-3">
                <Link
                href="/login"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center px-4 py-3 rounded-xl border border-border bg-muted/40 text-foreground font-semibold text-sm hover:border-primary/50 hover:text-primary transition-all touch-target">
                
                  {t.nav.signIn}
                </Link>
                <Link
                href="/register"
                onClick={() => setMobileMenuOpen(false)}
                className="flex items-center justify-center gap-2 px-4 py-3 rounded-xl gold-gradient text-primary-foreground font-bold text-sm hover:opacity-90 transition-all touch-target">
                
                  {t.nav.getStarted} {!isRtl && <ArrowRight size={14} />}
                </Link>
              </div>
              {/* Language switcher */}
              <div className="mt-6 pt-6 border-t border-border/50">
                <p className="text-xs text-muted-foreground mb-3 px-1">{lang === 'ar' ? 'اللغة' : lang === 'fr' ? 'Langue' : 'Language'}</p>
                <LangSwitcher lang={lang} setLang={(l) => {handleSetLang(l);}} />
              </div>
            </div>
          </div>
        }

        {/* ── Navbar ── */}
        <nav className="sticky top-0 z-50 border-b border-border/40 bg-background/85 backdrop-blur-xl" role="navigation" aria-label="Main navigation">
          <div className="max-w-7xl mx-auto px-4 sm:px-6 h-16 flex items-center justify-between gap-2">
            {/* Logo — always on the start side */}
            <div className={`flex items-center gap-2 flex-shrink-0 ${isRtl ? 'order-last' : 'order-first'}`}>
              <AppLogo size={28} />
              <span className="text-sm font-bold text-foreground">Wealth<span className="text-foreground">OS</span></span>
            </div>
            {/* Nav links — center, desktop only */}
            <div className="hidden md:flex items-center gap-4 lg:gap-6 flex-1 justify-center">
              {[['#features', t.nav.features], ['#how-it-works', t.nav.howItWorks], ['#pricing', t.nav.pricing], ['#testimonials', t.nav.reviews], ['#faq', t.nav.faq]].map(([href, label]) =>
              <a key={href} href={href} className="text-xs font-medium text-muted-foreground hover:text-primary transition-colors whitespace-nowrap">{label}</a>
              )}
            </div>
            {/* Actions — always on the end side */}
            <div className={`flex items-center gap-1.5 sm:gap-2 flex-shrink-0 ${isRtl ? 'order-first' : 'order-last'}`}>
              {/* Lang switcher — hidden on very small screens, shown xs+ */}
              <div className="hidden xs:block">
                <LangSwitcher lang={lang} setLang={handleSetLang} />
              </div>
              <button
                onClick={toggleTheme}
                className="flex items-center justify-center w-9 h-9 rounded-lg border border-border bg-muted/60 hover:border-primary/50 transition-all text-muted-foreground hover:text-foreground touch-target"
                aria-label="Toggle theme">
                {isDark ? <Sun size={14} /> : <Moon size={14} />}
              </button>
              <Link href="/login" className="hidden sm:flex px-3 py-1.5 text-xs font-semibold text-foreground rounded-lg border border-border hover:border-primary/50 hover:text-primary transition-all whitespace-nowrap">
                {t.nav.signIn}
              </Link>
              <Link href="/register" className="hidden sm:flex items-center gap-1 px-3 py-2 rounded-lg gold-gradient text-primary-foreground font-semibold text-xs hover:opacity-90 transition-all hover:-translate-y-0.5 active:scale-95 btn-gold-glow whitespace-nowrap">
                {t.nav.getStarted} {!isRtl && <ArrowRight size={12} />}
              </Link>
              {/* Mobile hamburger */}
              <button
                className="md:hidden w-9 h-9 rounded-lg bg-muted/60 flex items-center justify-center text-muted-foreground hover:text-foreground transition-colors touch-target"
                onClick={() => setMobileMenuOpen(true)}
                aria-label="Open navigation menu"
                aria-expanded={mobileMenuOpen}>
                
                <Menu size={18} />
              </button>
            </div>
          </div>
        </nav>

        {/* ── SECTION 1: HERO ── */}
        <section id="main-content" className="max-w-7xl mx-auto px-4 sm:px-6 pt-12 pb-16 sm:pt-16 sm:pb-20 lg:pt-24 hero-section-landscape">
          <div className={`grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-16 items-center`}>
            {/* Text side */}
            <div className={isRtl ? 'lg:order-2' : 'lg:order-1'}>
              <ScrollReveal>
                <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-primary/30 bg-primary/10 mb-5 sm:mb-6">
                  <Sparkles size={12} className="text-amber-700 dark:text-primary flex-shrink-0" />
                  <span className="text-xs font-semibold text-amber-800 dark:text-primary leading-tight">{t.hero.badge}</span>
                </div>
              </ScrollReveal>
              <ScrollReveal delay={100}>
                <h1 className={`hero-heading font-bold text-foreground mb-5 sm:mb-6 ${isRtl ? 'text-right' : 'text-left'}`}>
                  {getText('hero', 'title', '') || <>{t.hero.headline1}<br />{t.hero.headline2}<br /></>}
                  {getText('hero', 'title', '') ? null :
                  <span className="text-primary relative">
                      {t.hero.headline3}
                      <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-primary/60 to-transparent rounded-full" />
                    </span>
                  }
                  {getText('hero', 'title', '') &&
                  <span className="text-primary relative">
                      {getText('hero', 'title', '')}
                      <span className="absolute -bottom-1 left-0 right-0 h-0.5 bg-gradient-to-r from-primary/60 to-transparent rounded-full" />
                    </span>
                  }
                </h1>
              </ScrollReveal>
              <ScrollReveal delay={200}>
                <p className={`section-sub text-muted-foreground leading-relaxed mb-7 sm:mb-8 max-w-lg ${isRtl ? 'text-right' : 'text-left'}`}>
                  {getText('hero', 'subtitle', '') || t.hero.sub}
                </p>
              </ScrollReveal>
              <ScrollReveal delay={300}>
                <div className={`flex flex-col xs:flex-row gap-3 mb-6 ${isRtl ? 'xs:flex-row-reverse' : ''}`}>
                  <Link
                    href="/register"
                    className="relative flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl gold-gradient text-primary-foreground font-bold text-sm hover:opacity-90 transition-all hover:-translate-y-1 active:scale-95 overflow-hidden group shadow-lg shadow-primary/25 btn-gold-glow animate-glow-pulse touch-target">
                    <span className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/25 to-transparent animate-cta-shimmer pointer-events-none" />
                    <span className="relative z-10 text-center leading-tight">{t.hero.cta1}</span>
                    {!isRtl && <ArrowRight size={16} className="flex-shrink-0 relative z-10" />}
                  </Link>
                  <a
                    href="#how-it-works"
                    className="flex items-center justify-center gap-2 px-6 py-3.5 rounded-xl border border-border bg-muted/40 text-foreground font-semibold text-sm hover:bg-muted/70 hover:-translate-y-0.5 hover:border-primary/30 transition-all touch-target">
                    <span className="text-center leading-tight">{t.hero.cta2}</span>
                    {!isRtl && <ChevronRight size={16} className="flex-shrink-0" />}
                  </a>
                </div>
              </ScrollReveal>
              <ScrollReveal delay={400}>
                <div className={`flex flex-wrap gap-3 sm:gap-4 ${isRtl ? 'justify-end' : ''}`}>
                  {t.hero.trust.map((item) =>
                  <div key={item} className="flex items-center gap-1.5">
                      <div className="w-4 h-4 rounded-full bg-[#22c55e]/20 flex items-center justify-center flex-shrink-0">
                        <Check size={10} className="text-[#22c55e]" />
                      </div>
                      <span className="text-xs text-muted-foreground">{item}</span>
                    </div>
                  )}
                </div>
              </ScrollReveal>
            </div>

            {/* Image side — lifestyle photo with integrated dashboard */}
            <ScrollReveal delay={200} className={`relative ${isRtl ? 'lg:order-1' : 'lg:order-2'}`}>
              {/* Main lifestyle photo */}
              <div className="relative rounded-3xl overflow-hidden aspect-[4/3] shadow-2xl">
                <img
                  src="https://img.rocket.new/generatedImages/rocket_gen_img_1993192c0-1767000199933.png"
                  alt="Young professional couple planning finances together at a modern home desk with laptop"
                  className="w-full h-full object-cover"
                  loading="eager" />
                <div className="absolute inset-0 bg-gradient-to-t from-background/70 via-background/20 to-transparent" />
                {/* Floating mini widget top-right inside photo */}
                <div className="hero-image-overlay-widget absolute top-4 right-4 glass-card rounded-2xl border border-primary/40 px-3 py-2 backdrop-blur-xl animate-float shadow-xl" style={{ animationDelay: '0.5s' }}>
                  <p className="text-[9px] text-muted-foreground">{t.dashboard.netWorth}</p>
                  <p className="text-sm font-bold text-primary">$42,680</p>
                  <p className="text-[9px] text-[#22c55e]">↑ +6.42%</p>
                </div>
                {/* Floating income widget top-left */}
                <div className="hero-image-overlay-widget absolute top-4 left-4 glass-card rounded-2xl border border-[#22c55e]/30 px-3 py-2 backdrop-blur-xl animate-float shadow-xl" style={{ animationDelay: '1.2s' }}>
                  <p className="text-[9px] text-muted-foreground">{t.dashboard.income}</p>
                  <p className="text-sm font-bold text-[#22c55e]">$6,420</p>
                </div>
              </div>
              {/* Dashboard overlay — positioned below/overlapping photo on lg+, stacked on mobile */}
              <div className={`hero-dashboard-wrapper absolute -bottom-6 ${isRtl ? '-left-4 sm:-left-8' : '-left-4 sm:-left-8'} w-[240px] sm:w-[280px] lg:w-[300px]`}>
                <HeroDashboard notifText={t.hero.notif} db={t.dashboard} />
              </div>
            </ScrollReveal>
          </div>

          {/* Stats row */}
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-3 sm:gap-4 mt-20 sm:mt-24">
            {t.stats.map((s, i) =>
            <ScrollReveal key={s.label} delay={i * 80}>
                <div className="glass-card rounded-2xl p-4 sm:p-5 text-center border border-border card-hover-lift cursor-default">
                  <p className="text-xl sm:text-2xl font-bold text-primary mb-1">
                    <AnimatedNumber target={s.value} prefix={s.prefix} suffix={s.suffix} decimals={s.value % 1 !== 0 ? 1 : 0} />
                  </p>
                  <p className="text-xs text-muted-foreground font-medium leading-tight">{s.label}</p>
                </div>
              </ScrollReveal>
            )}
          </div>
        </section>

        <SectionDivider />

        {/* ── SECTION 2: PROBLEM ── */}
        <section className="py-16 sm:py-20 bg-muted/20 border-y border-border/40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <ScrollReveal>
              <div className="text-center mb-10 sm:mb-14">
                <h2 className="section-heading font-bold text-foreground mb-4">
                  {t.problem.headline.split('.')[0]}.<br />
                  <span className="text-primary">{t.problem.headline.split('.').slice(1).join('.').trim()}</span>
                </h2>
                <p className="section-sub text-muted-foreground max-w-lg mx-auto">{t.problem.sub}</p>
              </div>
            </ScrollReveal>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-5 sm:gap-6">
              {t.problem.items.map((item, i) =>
              <ScrollReveal key={i} delay={i * 120}>
                  <div className="relative glass-card rounded-3xl p-6 sm:p-8 border border-border card-hover-lift overflow-hidden group">
                    <div className="absolute top-0 right-0 w-32 h-32 rounded-full blur-2xl pointer-events-none opacity-30 group-hover:opacity-60 transition-all" style={{ backgroundColor: item.color }} />
                    <div className="absolute top-4 right-4 w-2 h-2 rounded-full opacity-60" style={{ backgroundColor: item.color }} />
                    <div className="text-3xl sm:text-4xl mb-4 sm:mb-5">{item.icon}</div>
                    <h3 className="text-base sm:text-lg font-bold text-foreground mb-2 sm:mb-3">{item.title}</h3>
                    <p className="text-sm text-muted-foreground leading-relaxed">{item.desc}</p>
                    <div className="mt-4 h-0.5 rounded-full opacity-30 group-hover:opacity-70 transition-opacity" style={{ backgroundColor: item.color }} />
                  </div>
                </ScrollReveal>
              )}
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* ── SECTION 3: SOLUTION / MEET WEALTHOS ── */}
        <section id="features" className="py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <ScrollReveal>
              <div className="text-center mb-10 sm:mb-14">
                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3">{t.solution.badge}</p>
                <h2 className="section-heading font-bold text-foreground mb-4">{t.solution.headline}</h2>
                <p className="section-sub text-muted-foreground max-w-xl mx-auto leading-relaxed">{t.solution.sub}</p>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={100}>
              <div className="overflow-x-hidden">
                <LargeDashboard insightText={t.solution.insight} db={t.dashboard} />
              </div>
            </ScrollReveal>
          </div>
        </section>

        <SectionDivider />

        {/* ── SECTION 4: BENEFITS ── */}
        <section className="py-16 sm:py-20 bg-muted/20 border-y border-border/40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <ScrollReveal>
              <div className="text-center mb-10 sm:mb-14">
                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3">{t.benefits.badge}</p>
                <h2 className="section-heading font-bold text-foreground">{t.benefits.headline}</h2>
              </div>
            </ScrollReveal>
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-5 sm:gap-6">
              {t.benefits.items.map((item, i) => {
                const icons = [TrendingUp, PiggyBank, FileText, Target];
                const BIcon = icons[i];
                const ml = t.benefits.miniLabels;
                const miniUIs = [
                <div key="nw" className="mt-4 rounded-xl bg-muted/40 p-3 border border-border/50">
                    <p className="text-[9px] text-muted-foreground mb-1">{ml.netWorth}</p>
                    <p className="text-lg font-bold" style={{ color: item.color }}>$42,680</p>
                    <div className="flex items-end gap-0.5 h-6 mt-2">
                      {[40, 55, 48, 70, 65, 85].map((h, idx) =>
                    <div key={idx} className="flex-1 rounded-sm" style={{ height: `${h}%`, backgroundColor: `${item.color}60` }} />
                    )}
                    </div>
                  </div>,
                <div key="sav" className="mt-4 rounded-xl bg-muted/40 p-3 border border-border/50">
                    <p className="text-[9px] text-muted-foreground mb-1">{ml.autoSavingsGoal}</p>
                    <div className="h-2 rounded-full bg-border mb-1 overflow-hidden">
                      <div className="h-full rounded-full progress-bar-fill" style={{ width: '68%', backgroundColor: item.color }} />
                    </div>
                    <p className="text-[9px]" style={{ color: item.color }}>$6,800 / $10,000 — 68%</p>
                  </div>,
                <div key="rep" className="mt-4 rounded-xl bg-muted/40 p-3 border border-border/50 space-y-1.5">
                    {[ml.housing, ml.food, ml.transport].map((l, li) =>
                  <div key={l} className="flex items-center gap-2">
                        <div className="h-1.5 rounded-full flex-1 bg-border overflow-hidden">
                          <div className="h-full rounded-full progress-bar-fill" style={{ width: li === 0 ? '45%' : li === 1 ? '20%' : '15%', backgroundColor: item.color }} />
                        </div>
                        <span className="text-[9px] text-muted-foreground w-20">{l}</span>
                      </div>
                  )}
                  </div>,
                <div key="goal" className="mt-4 rounded-xl bg-muted/40 p-3 border border-border/50 space-y-2">
                    {[{ l: ml.dreamHome, p: 73 }, { l: ml.travel, p: 48 }].map((g) =>
                  <div key={g.l}>
                        <div className="flex justify-between text-[9px] text-muted-foreground mb-0.5">
                          <span>{g.l}</span><span style={{ color: item.color }}>{g.p}%</span>
                        </div>
                        <div className="h-1.5 rounded-full bg-border overflow-hidden">
                          <div className="h-full rounded-full progress-bar-fill" style={{ width: `${g.p}%`, backgroundColor: item.color }} />
                        </div>
                      </div>
                  )}
                  </div>];


                return (
                  <ScrollReveal key={i} delay={i * 100}>
                    <div className="glass-card rounded-3xl p-8 border border-border card-hover-lift group relative overflow-hidden">
                      <div className="absolute top-0 right-0 w-40 h-40 rounded-full blur-3xl pointer-events-none transition-all group-hover:opacity-100 opacity-40" style={{ backgroundColor: `${item.color}15` }} />
                      <div className="flex items-start gap-5">
                        <div className="w-14 h-14 rounded-2xl flex items-center justify-center flex-shrink-0 transition-transform group-hover:scale-110 group-hover:rotate-3" style={{ backgroundColor: `${item.color}20` }}>
                          <BIcon size={24} style={{ color: item.color }} />
                        </div>
                        <div className="flex-1">
                          <h3 className="text-lg font-bold text-foreground mb-2">{item.title}</h3>
                          <p className="text-sm text-muted-foreground leading-relaxed mb-3">{item.desc}</p>
                          <span className="inline-flex items-center gap-1.5 text-xs font-semibold px-3 py-1.5 rounded-full border" style={{ color: item.color, borderColor: `${item.color}40`, backgroundColor: `${item.color}10` }}>
                            <BarChart3 size={11} /> {item.label}
                          </span>
                          {miniUIs[i]}
                        </div>
                      </div>
                    </div>
                  </ScrollReveal>);

              })}
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* ── SECTION 5: LIFESTYLE GOALS ── */}
        <section className="py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <ScrollReveal>
              <div className="text-center mb-12 sm:mb-16">
                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3">{t.lifestyle.badge}</p>
                <h2 className="section-heading font-bold text-foreground mb-4">{t.lifestyle.headline}</h2>
                <p className="section-sub text-muted-foreground max-w-lg mx-auto">{t.lifestyle.sub}</p>
              </div>
            </ScrollReveal>
            <div className="grid grid-cols-2 sm:grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {t.lifestyle.cards.map((card, i) =>
              <LifestyleGoalCard
                key={i}
                card={card}
                complete={t.lifestyle.complete}
                delay={i * 120} />
              )}
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* ── SECTION 6: BEFORE / AFTER ── */}
        <section className="py-16 sm:py-20 bg-muted/20 border-y border-border/40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <ScrollReveal>
              <div className="text-center mb-10 sm:mb-14">
                <h2 className="section-heading font-bold text-foreground">{t.beforeAfter.headline}</h2>
                <p className="section-sub text-muted-foreground mt-3">{t.beforeAfter.sub}</p>
              </div>
            </ScrollReveal>
            <div className="grid grid-cols-1 md:grid-cols-[1fr_auto_1fr] gap-5 sm:gap-6 max-w-4xl mx-auto items-center">
              <ScrollReveal delay={0} direction="left">
                <div className="rounded-3xl border border-red-400/20 p-8 bg-red-400/5 relative overflow-hidden card-hover-lift">
                  <div className="absolute top-0 right-0 w-32 h-32 rounded-full bg-red-400/5 blur-2xl pointer-events-none" />
                  <p className="text-xs font-bold text-red-400/80 uppercase tracking-widest mb-6">{t.beforeAfter.before.title}</p>
                  <div className="space-y-4">
                    {t.beforeAfter.before.items.map((item, i) =>
                    <div key={i} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-red-400/10 border border-red-400/20 flex items-center justify-center flex-shrink-0">
                          <X size={12} className="text-red-400" />
                        </div>
                        <span className="text-sm text-muted-foreground">{item}</span>
                      </div>
                    )}
                  </div>
                </div>
              </ScrollReveal>
              <ScrollReveal delay={150} direction="scale">
                <div className="flex items-center justify-center">
                  <div className="w-12 h-12 rounded-full border-2 border-primary/40 bg-primary/10 flex items-center justify-center shadow-lg shadow-primary/20 animate-glow-pulse">
                    <span className="text-xs font-black text-primary">VS</span>
                  </div>
                </div>
              </ScrollReveal>
              <ScrollReveal delay={300} direction="right">
                <div className="rounded-3xl border border-primary/30 p-8 bg-primary/5 relative overflow-hidden card-hover-lift">
                  <div className="absolute inset-0 bg-gradient-to-br from-primary/5 to-transparent pointer-events-none" />
                  <div className="absolute top-4 right-4 w-2 h-2 rounded-full bg-[#22c55e] shadow-[0_0_6px_2px_rgba(34,197,94,0.4)]" />
                  <p className="text-xs font-bold text-primary uppercase tracking-widest mb-6">{t.beforeAfter.after.title}</p>
                  <div className="space-y-4">
                    {t.beforeAfter.after.items.map((item, i) =>
                    <div key={i} className="flex items-center gap-3">
                        <div className="w-6 h-6 rounded-full bg-[#22c55e]/15 border border-[#22c55e]/30 flex items-center justify-center flex-shrink-0">
                          <Check size={12} className="text-[#22c55e]" />
                        </div>
                        <span className="text-sm font-medium text-foreground">{item}</span>
                      </div>
                    )}
                  </div>
                </div>
              </ScrollReveal>
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* ── SECTION 7: HOW IT WORKS ── */}
        <section id="how-it-works" className="py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <ScrollReveal>
              <div className="text-center mb-10 sm:mb-14">
                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3">{t.howItWorks.badge}</p>
                <h2 className="section-heading font-bold text-foreground">{t.howItWorks.headline}</h2>
              </div>
            </ScrollReveal>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-8 relative">
              <div className="hidden md:block absolute top-10 left-[calc(16.67%+2rem)] right-[calc(16.67%+2rem)] h-px overflow-hidden">
                <div className="h-full bg-gradient-to-r from-primary/20 via-primary/60 to-primary/20" />
              </div>
              {t.howItWorks.steps.map((step, i) => {
                const stepIcons = [Globe, BarChart3, TrendingUp];
                const StepIcon = stepIcons[i];
                return (
                  <ScrollReveal key={i} delay={i * 150}>
                    <div className="text-center relative group">
                      <div className="relative inline-flex items-center justify-center w-20 h-20 rounded-3xl border border-primary/30 bg-primary/10 mb-6 mx-auto transition-all group-hover:border-primary/60 group-hover:bg-primary/20 group-hover:-translate-y-2 group-hover:shadow-lg group-hover:shadow-primary/20">
                        <StepIcon size={28} className="text-primary" />
                        <span className="absolute -top-2 -right-2 w-7 h-7 rounded-full gold-gradient text-primary-foreground text-xs font-bold flex items-center justify-center shadow-lg">
                          {i + 1}
                        </span>
                      </div>
                      <p className="text-5xl font-bold text-primary/10 mb-2">{step.num}</p>
                      <h3 className="text-xl font-bold text-foreground mb-3">{step.title}</h3>
                      <p className="text-sm text-muted-foreground leading-relaxed max-w-xs mx-auto">{step.desc}</p>
                    </div>
                  </ScrollReveal>);

              })}
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* ── SECTION 8: SAVINGS CALCULATOR ── */}
        <section className="py-16 sm:py-20 bg-muted/20 border-y border-border/40">
          <div className="max-w-5xl mx-auto px-4 sm:px-6">
            <ScrollReveal>
              <div className="text-center mb-10 sm:mb-12">
                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3">{t.calculator.badge}</p>
                <h2 className="section-heading font-bold text-foreground mb-4">{t.calculator.headline}</h2>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={100}>
              <SavingsCalculator t={t} />
            </ScrollReveal>
          </div>
        </section>

        <SectionDivider />

        {/* ── SECTION 9: TESTIMONIALS ── */}
        <section id="testimonials" className="py-16 sm:py-20">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <ScrollReveal>
              <div className="text-center mb-10 sm:mb-14">
                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3">{t.testimonials.badge}</p>
                <h2 className="section-heading font-bold text-foreground">{t.testimonials.headline}</h2>
              </div>
            </ScrollReveal>
            <ScrollReveal delay={100}>
              <div className="grid grid-cols-2 sm:grid-cols-4 gap-4 mb-12">
                {[
                { value: 10, suffix: 'K+', label: t.stats[0].label },
                { value: 2.4, suffix: 'B', label: t.stats[1].label, prefix: '$' },
                { value: 98, suffix: '%', label: t.stats[2].label },
                { value: 4.9, suffix: '★', label: t.stats[3].label }].
                map((s, i) =>
                <div key={i} className="glass-card rounded-2xl p-4 text-center border border-border card-hover-lift">
                    <p className="text-xl font-bold text-primary">
                      <AnimatedNumber target={s.value} prefix={s.prefix || ''} suffix={s.suffix} decimals={s.value % 1 !== 0 ? 1 : 0} />
                    </p>
                    <p className="text-xs text-muted-foreground">{s.label}</p>
                  </div>
                )}
              </div>
            </ScrollReveal>
            <ScrollReveal delay={200}>
              <TestimonialCarousel items={
              cmsTestimonials.length > 0 ?
              cmsTestimonials.map((ct) => ({
                name: ct.name,
                role: ct.job_title || '',
                stars: ct.rating,
                quote: getTestimonialText(ct, cmsLang),
                img: ct.photo_url || null,
                imgAlt: ct.name
              })) :
              t.testimonials.items
              } />
            </ScrollReveal>
          </div>
        </section>

        <SectionDivider />

        {/* ── SECTION 10: SECURITY ── */}
        <section className="py-14 sm:py-16 bg-muted/20 border-y border-border/40">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <ScrollReveal>
              <div className="text-center mb-10 sm:mb-12">
                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3">{t.security.badge}</p>
                <h2 className="text-2xl sm:text-3xl font-bold text-foreground">{t.security.headline}</h2>
              </div>
            </ScrollReveal>
            <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 sm:gap-5">
              {t.security.items.map((item, i) => {
                const ItemIcon = item.icon;
                return (
                  <ScrollReveal key={i} delay={i * 80}>
                    <div className="glass-card rounded-2xl p-6 border border-border text-center card-hover-lift group">
                      <div className="w-12 h-12 rounded-2xl flex items-center justify-center mx-auto mb-4 transition-transform group-hover:scale-110 group-hover:rotate-3" style={{ backgroundColor: `${item.color}20` }}>
                        <ItemIcon size={20} style={{ color: item.color }} />
                      </div>
                      <div className="flex items-center justify-center gap-1.5 mb-2">
                        <div className="w-1.5 h-1.5 rounded-full" style={{ backgroundColor: item.color }} />
                        <p className="text-sm font-bold text-foreground">{item.title}</p>
                      </div>
                      <p className="text-xs text-muted-foreground">{item.desc}</p>
                    </div>
                  </ScrollReveal>);

              })}
            </div>
          </div>
        </section>

        {/* ── SECTION 11: PRICING ── */}
        <PricingSection lang={lang} isRtl={isRtl} />

        {/* ── SECTION 12: FAQ ── */}
        <section id="faq" className="py-16 sm:py-20">
          <div className="max-w-3xl mx-auto px-4 sm:px-6">
            <ScrollReveal>
              <div className="text-center mb-10 sm:mb-12">
                <p className="text-xs font-bold text-primary uppercase tracking-widest mb-3">{t.faq.badge}</p>
                <h2 className="section-heading font-bold text-foreground">{t.faq.headline}</h2>
              </div>
            </ScrollReveal>
            <div className="space-y-3">
              {(cmsFaqItems.length > 0 ?
              cmsFaqItems.map((fi) => {
                const { question, answer } = getFaqText(fi, cmsLang);
                return { q: question, a: answer };
              }) :
              t.faq.items).
              map((faq, i) =>
              <ScrollReveal key={faq.q} delay={i * 60}>
                  <FAQItem q={faq.q} a={faq.a} isRtl={isRtl} />
                </ScrollReveal>
              )}
            </div>
          </div>
        </section>

        <SectionDivider />

        {/* ── SECTION 13: FINAL CTA ── */}
        <section className="py-16 sm:py-20 pb-24 sm:pb-28">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            <ScrollReveal>
              <div className="relative rounded-3xl overflow-hidden border border-primary/30 p-8 sm:p-12 lg:p-20 text-center">
                <div className="absolute inset-0 bg-gradient-to-br from-primary/8 via-transparent to-[#22c55e]/5 pointer-events-none" />
                <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[600px] h-[600px] rounded-full bg-primary/5 blur-3xl pointer-events-none" />
                {/* Background lifestyle image */}
                <div className="absolute inset-0 opacity-8">
                  <img
                    src="https://images.unsplash.com/photo-1551836022-deb4988cc6c0?w=1400&q=60"
                    alt=""
                    className="w-full h-full object-cover"
                    aria-hidden="true" />
                  <div className="absolute inset-0 bg-background/80" />
                </div>
                <div className="relative z-10">
                  <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-primary/30 bg-primary/10 mb-5 sm:mb-6">
                    <Gift size={13} className="text-primary flex-shrink-0" />
                    <span className="text-xs font-semibold text-primary">{t.hero.trust[0]} — {t.hero.trust[1]}</span>
                  </div>
                  <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-foreground mb-4 sm:mb-5 leading-tight">
                    {t.finalCta.headline}
                  </h2>
                  <p className="section-sub text-muted-foreground max-w-lg mx-auto leading-relaxed mb-8 sm:mb-10">
                    {t.finalCta.sub}
                  </p>
                  <div className={`flex flex-col xs:flex-row items-center justify-center gap-3 sm:gap-4 mb-5 sm:mb-6 ${isRtl ? 'xs:flex-row-reverse' : ''}`}>
                    <Link
                      href="/register"
                      className="relative flex items-center justify-center gap-2 w-full xs:w-auto px-8 py-4 rounded-xl gold-gradient text-primary-foreground font-bold text-sm sm:text-base hover:opacity-90 hover:-translate-y-1 transition-all active:scale-95 shadow-2xl shadow-primary/30 btn-gold-glow overflow-hidden group touch-target">
                      <span className="absolute inset-0 w-1/3 bg-gradient-to-r from-transparent via-white/20 to-transparent animate-cta-shimmer pointer-events-none" />
                      <span className="relative z-10">{t.finalCta.cta1}</span>
                    </Link>
                    <Link
                      href="/login"
                      className="flex items-center justify-center gap-2 w-full xs:w-auto px-8 py-4 rounded-xl border border-border bg-muted/50 text-foreground font-semibold text-sm sm:text-base hover:bg-muted/80 hover:-translate-y-0.5 hover:border-primary/30 transition-all touch-target">
                      {t.finalCta.cta2}
                    </Link>
                  </div>
                  <div className="flex flex-wrap items-center justify-center gap-3 sm:gap-4 text-xs text-muted-foreground">
                    {t.finalCta.trust.map((item) =>
                    <div key={item} className="flex items-center gap-1.5">
                        <Check size={12} className="text-[#22c55e] flex-shrink-0" />
                        <span>{item}</span>
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </ScrollReveal>
          </div>
        </section>

        {/* ── Footer ── */}
        <footer className="border-t border-border/50 py-10 sm:py-12" role="contentinfo">
          <div className="max-w-7xl mx-auto px-4 sm:px-6">
            {/* Top row */}
            <div className={`flex flex-col sm:flex-row items-center justify-between gap-6 mb-8 ${isRtl ? 'sm:flex-row-reverse' : ''}`}>
              <div className="flex items-center gap-2 flex-shrink-0">
                <AppLogo size={28} />
                <span className="text-base font-bold text-foreground">Wealth<span className="text-foreground">OS</span></span>
              </div>
              <p className="text-xs text-muted-foreground text-center">
                {isRtl ? 'نظام التشغيل المالي الشخصي' : lang === 'fr' ? 'Votre système d\'exploitation financier personnel' : 'Your Personal Financial Operating System'}
              </p>
              <div className={`flex items-center gap-1 bg-muted/40 rounded-xl p-1 ${isRtl ? 'flex-row-reverse' : ''}`}>
                {(['en', 'ar', 'fr'] as Lang[]).map((l) =>
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  aria-label={`Switch to ${l.toUpperCase()}`}
                  aria-pressed={lang === l}
                  className={`px-3 py-1.5 rounded-lg text-xs font-bold transition-all ${
                  lang === l ? 'bg-foreground text-background shadow' : 'text-muted-foreground hover:text-foreground'}`
                  }>
                  
                    {l.toUpperCase()}
                  </button>
                )}
              </div>
            </div>
            {/* Links row */}
            <div className={`flex flex-wrap items-center justify-center gap-x-5 gap-y-2 text-xs text-muted-foreground border-t border-border/40 pt-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
              <Link href="/privacy" className="hover:text-foreground transition-colors">{t.footer.privacy}</Link>
              <Link href="/terms" className="hover:text-foreground transition-colors">{t.footer.terms}</Link>
              <Link href="/cookie-policy" className="hover:text-foreground transition-colors">{t.footer.cookies}</Link>
              <Link href="/refund-policy" className="hover:text-foreground transition-colors">{t.footer.refund}</Link>
              <Link href="/support" className="hover:text-foreground transition-colors">{t.footer.support}</Link>
              <span className="text-muted-foreground/40">·</span>
              <span>© 2026 WealthOS. {t.footer.rights}</span>
            </div>
          </div>
        </footer>
      </div>
    </div>);

}